import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes, ThemeProvider } from "styled-components";
import {
  adminGetStats, adminGetUsers, adminSetRole, adminToggleActive,
  adminGetMarkets, adminCreateMarket, adminUpdateMarket, adminDeleteMarket,
  adminPublishMarket, adminCloseMarket, adminResolveMarket, adminCancelMarket,
  adminGetMarketPriorityAuto, adminSetMarketPriorityAuto,
  adminGetUXByCountry,
  adminGetCategories, adminCreateCategory, adminDeleteCategory,
  adminGetWithdrawals, adminApproveWithdrawal, adminRejectWithdrawal,
    adminGetPayouts,
    adminApprovePayout,
    adminCompletePayout,
    adminRejectPayout,
      adminGetReferralStats,
  adminGetReferralList,
  adminGetReferralDetail,
  adminApproveCommission,
  adminRejectCommission,
  adminFlagRelation,
  adminAutoTranslate,
  adminUpdateCategory, 
  
} from "./adminClient";
import EmailCampaignsTab from "./EmailCampaignsTab";

/* ─── Theme ─────────────────────────────────────────────────────────────── */
const theme = {
  bg:"#080a0c", bgCard:"#0e1115", bgHover:"#141820",
  border:"#1e2530", borderLight:"#2a3340",
  green:"#00e676", greenDim:"#00e67622",
  red:"#ff3b5c",   redDim:"#ff3b5c22",
  gold:"#ffc107",  accent:"#4fc3f7",
  text:"#e8edf2",  textMuted:"#5a6a7a", textDim:"#8a9aaa",
  fontMono:"'DM Mono','Courier New',monospace",
  fontDisplay:"'Playfair Display',Georgia,serif",
  fontBody:"'Syne',sans-serif",
};

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn  = keyframes`from{opacity:0}to{opacity:1}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;
const pulse    = keyframes`0%,100%{opacity:.4}50%{opacity:1}`;   // ← AJOUTER
/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Shell = styled.div`
  min-height: 100vh;
  background: ${({theme}) => theme.bg};
  color: ${({theme}) => theme.text};
  font-family: ${({theme}) => theme.fontBody};
  display: flex;
  background-image: radial-gradient(ellipse 60% 40% at 70% 0%, #0d1a2a 0%, transparent 60%);
`;

/* ── Sidebar ───────────────────────────────────────────────────────────── */
const Sidebar = styled.aside`
  width: 220px;
  min-height: 100vh;
  background: ${({theme}) => theme.bgCard};
  border-right: 1px solid ${({theme}) => theme.border};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  z-index: 150;
  transition: transform .25s ease;

  /* Desktop collapsed state */
  @media (min-width: 769px) {
    transform: ${({$collapsed}) => $collapsed ? "translateX(-220px)" : "translateX(0)"};
    margin-left: ${({$collapsed}) => $collapsed ? "-220px" : "0"};
    transition: transform .25s ease, margin-left .25s ease;
  }

  /* Mobile: always off-canvas, slide in when open */
  @media (max-width: 768px) {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    transform: ${({$open}) => $open ? "translateX(0)" : "translateX(-100%)"};
    box-shadow: ${({$open}) => $open ? "4px 0 24px rgba(0,0,0,.5)" : "none"};
  }
`;

/* Overlay to close sidebar on mobile */
const SideOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({$open}) => $open ? "block" : "none"};
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,.6);
    z-index: 149;
    animation: ${fadeIn} .2s ease;
  }
`;

/* ── Toggle button (always visible) ────────────────────────────────────── */
const ToggleBtn = styled.button`
  position: fixed;
  top: .85rem;
  left: ${({$collapsed, $open}) => (!$collapsed || $open) ? "228px" : "1rem"};
  z-index: 200;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.border};
  background: ${({theme}) => theme.bgCard};
  color: ${({theme}) => theme.textMuted};
  font-size: .85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: left .25s ease, color .15s, border-color .15s;
  box-shadow: 0 2px 8px rgba(0,0,0,.3);

  &:hover {
    color: ${({theme}) => theme.text};
    border-color: ${({theme}) => theme.borderLight};
  }

  @media (max-width: 768px) {
    left: ${({$open}) => $open ? "228px" : "1rem"};
    top: .85rem;
  }
`;

/* ── Main content ── */
const Main = styled.main`
  flex: 1;
  padding: 2rem 2rem 2rem 3.5rem;
  overflow-x: hidden;
  min-width: 0;
  transition: padding .25s ease;

  @media (max-width: 768px) {
    padding: 3.5rem 1rem 1rem;
  }
`;
const Required = styled.span`color:${({theme})=>theme.red};font-size:.8rem;`;

/* ─── Sidebar internals ────────────────────────────────────────────────── */
const SideTop = styled.div`
  padding: 1.25rem 1rem 1rem;
  border-bottom: 1px solid ${({theme}) => theme.border};
`;
const SideLogo = styled.div`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.1rem; font-weight: 700; letter-spacing: -.02em; margin-bottom: .2rem;
  cursor: pointer; transition: opacity .15s;
  span { color: ${({theme}) => theme.gold}; }
  &:hover { opacity: .75; }
`;
const SideRole = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .6rem; letter-spacing: .08em; text-transform: uppercase;
  color: ${({theme}) => theme.textMuted};
`;
const InlineTranslate = styled.button`
  display: inline-flex; align-items: center; gap: .25rem;
  padding: .2rem .55rem; border-radius: 5px;
  border: 1px solid ${({theme}) => theme.accent}44;
  background: rgba(79,195,247,.06);
  color: ${({theme}) => theme.accent};
  font-family: ${({theme}) => theme.fontMono};
  font-size: .58rem; font-weight: 600; letter-spacing: .04em;
  cursor: pointer; transition: all .15s;
  &:hover:not(:disabled) { background: rgba(79,195,247,.15); }
  &:disabled { opacity: .5; cursor: wait; }
`;
const Nav = styled.nav`padding: .75rem 0; flex: 1;`;
const NavItem = styled.button`
  width: 100%; display: flex; align-items: center; gap: .65rem;
  padding: .65rem 1rem; background: none; border: none;
  font-family: ${({theme}) => theme.fontBody}; font-size: .8rem; font-weight: 500;
  cursor: pointer; transition: all .15s; text-align: left;
  border-left: 2px solid ${({$active, theme}) => $active ? theme.gold : "transparent"};
  background: ${({$active}) => $active ? "rgba(255,193,7,.06)" : "transparent"};
  color: ${({$active, theme}) => $active ? theme.text : theme.textDim};
  &:hover { color: ${({theme}) => theme.text}; background: ${({theme}) => theme.bgHover}; }
`;
const NavIcon  = styled.span`font-size: 1rem; width: 18px; text-align: center;`;
const NavBadge = styled.span`
  margin-left: auto; font-family: ${({theme}) => theme.fontMono}; font-size: .58rem;
  background: ${({$red, theme}) => $red ? theme.red : theme.bgHover};
  color: ${({$red}) => $red ? "#fff" : "inherit"};
  border-radius: 100px; padding: .1rem .45rem;
`;
const SideFooter = styled.div`padding: .75rem 1rem; border-top: 1px solid ${({theme}) => theme.border};`;
const UserChip   = styled.div`display: flex; align-items: center; gap: .5rem; font-size: .75rem; color: ${({theme}) => theme.textDim};`;
const UserDot    = styled.div`
  width: 28px; height: 28px; border-radius: 50%;
  background: ${({theme}) => theme.bgHover}; border: 1px solid ${({theme}) => theme.border};
  display: flex; align-items: center; justify-content: center;
  font-family: ${({theme}) => theme.fontMono}; font-size: .62rem; font-weight: 600;
  color: ${({theme}) => theme.gold};
`;
const SignOutBtn = styled.button`
  margin-top: .5rem; width: 100%; padding: .4rem; border-radius: 6px;
  background: transparent; border: 1px solid ${({theme}) => theme.border};
  color: ${({theme}) => theme.textMuted}; font-size: .72rem; cursor: pointer;
  font-family: ${({theme}) => theme.fontBody}; transition: all .15s;
  &:hover { border-color: ${({theme}) => theme.red}55; color: ${({theme}) => theme.red}; }
`;

/* ─── Page header ─────────────────────────────────────────────────────── */
const PageTitle = styled.h1`
  font-family: ${({theme}) => theme.fontDisplay}; font-size: 1.6rem; font-weight: 700;
  letter-spacing: -.03em; margin-bottom: .3rem;
  span { color: ${({theme}) => theme.gold}; }
  @media (max-width: 480px) { font-size: 1.3rem; }
`;
const PageSub = styled.p`font-size: .78rem; color: ${({theme}) => theme.textMuted}; margin-bottom: 2rem;`;

/* ─── Stats ───────────────────────────────────────────────────────────── */
const StatGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 2rem;
  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; }
`;
const StatCard = styled.div`
  background: ${({theme}) => theme.bgCard}; border: 1px solid ${({theme}) => theme.border};
  border-radius: 12px; padding: 1rem 1.25rem;
  animation: ${fadeUp} .4s ease both; animation-delay: ${({$i}) => $i * .06}s;
`;
const StatVal = styled.div`
  font-family: ${({theme}) => theme.fontMono}; font-size: 1.15rem; font-weight: 500;
  color: ${({$c, theme}) => theme[$c] || theme.text};
`;
const StatLbl = styled.div`font-size: .65rem; text-transform: uppercase; letter-spacing: .08em; color: ${({theme}) => theme.textMuted}; margin-top: .2rem;`;

const LiqPresets = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: .5rem;
  @media (min-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;
const LiqPreset = styled.button`
  display: flex; flex-direction: column; align-items: flex-start;
  gap: .15rem; padding: .6rem .75rem;
  border-radius: 8px; cursor: pointer; transition: all .15s;
  background: ${({$active}) => $active ? "rgba(79,195,247,.08)" : "transparent"};
  border: 1px solid ${({$active, theme}) => $active ? theme.accent + "88" : theme.border};
  text-align: left;
  &:hover { border-color: ${({theme}) => theme.borderLight}; }
`;
const LiqPresetLabel = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .6rem; letter-spacing: .06em;
  color: ${({$active, theme}) => $active ? theme.accent : theme.textMuted};
  text-transform: uppercase;
`;
const LiqPresetValue = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .82rem; font-weight: 700;
  color: ${({$active, theme}) => $active ? theme.accent : theme.text};
`;
const LiqPresetHint = styled.span`
  font-size: .62rem; color: ${({theme}) => theme.textMuted};
  line-height: 1.25;
`;
const LiqInfo = styled.div`
  display: flex; align-items: flex-start; gap: .4rem;
  padding: .55rem .75rem;
  background: rgba(79,195,247,.05);
  border: 1px solid ${({theme}) => theme.accent}22;
  border-radius: 7px;
  font-size: .7rem; color: ${({theme}) => theme.textDim};
  line-height: 1.5;
  & strong { color: ${({theme}) => theme.accent}; font-family: ${({theme}) => theme.fontMono}; }
`;

/* ─── Panel / Table ───────────────────────────────────────────────────── */
const Panel       = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};border-radius:12px;overflow:hidden;margin-bottom:1.5rem;`;
const PanelHead   = styled.div`display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;padding:.85rem 1.25rem;background:${({theme})=>theme.bg};border-bottom:1px solid ${({theme})=>theme.border};`;
const PanelTitle  = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.67rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const PanelActions= styled.div`display:flex;gap:.5rem;align-items:center;flex-wrap:wrap;`;
const FilterChip  = styled.button`font-family:${({theme})=>theme.fontMono};font-size:.62rem;padding:.22rem .6rem;border-radius:100px;letter-spacing:.05em;cursor:pointer;transition:all .15s;border:1px solid ${({$active,theme})=>$active?theme.borderLight:theme.border};background:${({$active,theme})=>$active?theme.bgHover:"transparent"};color:${({$active,theme})=>$active?theme.text:theme.textMuted};&:hover{border-color:${({theme})=>theme.borderLight};}`;
const Search      = styled.input`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:7px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};font-size:.75rem;padding:.35rem .75rem;outline:none;width:160px;&:focus{border-color:${({theme})=>theme.borderLight};}&::placeholder{color:${({theme})=>theme.textMuted};}`;
const NewBtn      = styled.button`display:flex;align-items:center;gap:.35rem;padding:.4rem .9rem;border-radius:7px;border:1px solid ${({theme})=>theme.gold}66;background:rgba(255,193,7,.08);color:${({theme})=>theme.gold};font-family:${({theme})=>theme.fontBody};font-size:.75rem;font-weight:600;cursor:pointer;transition:all .15s;&:hover{opacity:.8;}`;
const TableScroll = styled.div`overflow-x:auto;-webkit-overflow-scrolling:touch;`;
const THead       = styled.div`display:grid;grid-template-columns:${({$cols})=>$cols};gap:.5rem;padding:.6rem 1.25rem;background:${({theme})=>theme.bg};border-bottom:1px solid ${({theme})=>theme.border};min-width:${({$minW})=>$minW||"auto"};`;
const TH          = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const TRow        = styled.div`display:grid;grid-template-columns:${({$cols})=>$cols};gap:.5rem;padding:.85rem 1.25rem;border-bottom:1px solid ${({theme})=>theme.border};align-items:center;transition:background .15s;min-width:${({$minW})=>$minW||"auto"};&:last-child{border-bottom:none;}&:hover{background:${({theme})=>theme.bgHover};}`;
const Cell        = styled.div`font-size:.78rem;color:${({$dim,theme})=>$dim?theme.textDim:theme.text};font-family:${({$mono,theme})=>$mono?theme.fontMono:"inherit"};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
const Pill        = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.18rem .55rem;border-radius:100px;letter-spacing:.05em;background:${({$s,theme})=>$s==="OPEN"||$s==="APPROVED"||$s==="active"?theme.greenDim:$s==="PENDING"||$s==="CLOSED"?"rgba(255,193,7,.1)":$s==="RESOLVED"?"rgba(79,195,247,.1)":$s==="DRAFT"?theme.bgHover:$s==="REJECTED"||$s==="CANCELLED"||$s==="inactive"?theme.redDim:theme.bgHover};color:${({$s,theme})=>$s==="OPEN"||$s==="APPROVED"||$s==="active"?theme.green:$s==="PENDING"||$s==="CLOSED"?theme.gold:$s==="RESOLVED"?theme.accent:$s==="DRAFT"?theme.textMuted:$s==="REJECTED"||$s==="CANCELLED"||$s==="inactive"?theme.red:theme.textMuted};`;
const ActionBtns  = styled.div`display:flex;gap:.35rem;flex-wrap:wrap;`;
const Btn         = styled.button`font-family:${({theme})=>theme.fontMono};font-size:.63rem;padding:.25rem .65rem;border-radius:5px;cursor:pointer;letter-spacing:.04em;transition:all .15s;white-space:nowrap;border:1px solid ${({$t,theme})=>$t==="ok"?theme.green+"55":$t==="warn"?theme.gold+"55":$t==="danger"?theme.red+"55":theme.borderLight};background:${({$t,theme})=>$t==="ok"?theme.greenDim:$t==="warn"?"rgba(255,193,7,.08)":$t==="danger"?theme.redDim:theme.bgHover};color:${({$t,theme})=>$t==="ok"?theme.green:$t==="warn"?theme.gold:$t==="danger"?theme.red:theme.textDim};&:disabled{opacity:.35;cursor:not-allowed;}&:hover:not(:disabled){opacity:.8;}`;
const Empty       = styled.div`text-align:center;padding:2.5rem;font-size:.8rem;color:${({theme})=>theme.textMuted};`;
const Skel        = styled.div`height:48px;margin:.3rem 1.25rem;border-radius:6px;background:linear-gradient(90deg,${({theme})=>theme.bgCard} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bgCard} 75%);background-size:400px 100%;animation:${shimmer} 1.4s infinite;`;

/* ─── Modal ────────────────────────────────────────────────────────────── */
const Overlay    = styled.div`position:fixed;inset:0;z-index:500;background:rgba(0,0,0,.82);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:1rem;`;
const ModalBox   = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.borderLight};border-radius:16px;width:100%;max-width:${({$w})=>$w||"520px"};max-height:90vh;overflow-y:auto;`;
const MHead      = styled.div`padding:1.1rem 1.4rem;border-bottom:1px solid ${({theme})=>theme.border};display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;background:${({theme})=>theme.bgCard};z-index:1;`;
const MTitle     = styled.h3`font-family:${({theme})=>theme.fontDisplay};font-size:1rem;font-weight:700;letter-spacing:-.02em;`;
const MClose     = styled.button`background:none;border:none;color:${({theme})=>theme.textMuted};font-size:1rem;cursor:pointer;&:hover{color:${({theme})=>theme.text};}`;
const MBody      = styled.div`padding:1.25rem 1.4rem;display:flex;flex-direction:column;gap:1rem;`;
const MFoot      = styled.div`padding:.85rem 1.4rem;border-top:1px solid ${({theme})=>theme.border};display:flex;gap:.6rem;justify-content:flex-end;`;
const Field      = styled.div`display:flex;flex-direction:column;gap:.3rem;`;
const Label      = styled.label`font-size:.67rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const Input      = styled.input`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:7px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};font-size:.82rem;padding:.55rem .8rem;outline:none;transition:border-color .15s;&:focus{border-color:${({theme})=>theme.borderLight};}&::placeholder{color:${({theme})=>theme.textMuted};}`;
const Tarea      = styled.textarea`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:7px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};font-size:.82rem;padding:.55rem .8rem;outline:none;resize:vertical;min-height:72px;&:focus{border-color:${({theme})=>theme.borderLight};}&::placeholder{color:${({theme})=>theme.textMuted};}`;
const Select     = styled.select`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:7px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};font-size:.82rem;padding:.55rem .8rem;outline:none;cursor:pointer;option{background:${({theme})=>theme.bgCard};}`;
const Row2       = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:.75rem;@media(max-width:480px){grid-template-columns:1fr;}`;
const CancelBtn2 = styled.button`padding:.55rem 1rem;border-radius:7px;border:1px solid ${({theme})=>theme.border};background:transparent;color:${({theme})=>theme.textDim};font-size:.78rem;font-weight:600;cursor:pointer;font-family:${({theme})=>theme.fontBody};&:hover{background:${({theme})=>theme.bgHover};}`;
const SaveBtn    = styled.button`padding:.55rem 1.2rem;border-radius:7px;border:none;background:${({theme,disabled})=>disabled?theme.bgHover:theme.gold};color:${({theme,disabled})=>disabled?theme.textMuted:"#000"};font-size:.78rem;font-weight:700;cursor:${({disabled})=>disabled?"not-allowed":"pointer"};font-family:${({theme})=>theme.fontBody};transition:opacity .15s;&:hover:not(:disabled){opacity:.88;}`;
const OcBlock    = styled.div`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:8px;padding:.85rem;display:flex;flex-direction:column;gap:.5rem;`;
const OcRow      = styled.div`display:grid;grid-template-columns:1fr 72px 28px;gap:.4rem;align-items:center;`;
const OcInput    = styled(Input)`padding:.45rem .65rem;font-size:.78rem;`;
const OcPct      = styled(Input)`text-align:center;padding:.45rem .4rem;font-size:.78rem;`;
const OcDel      = styled.button`width:26px;height:26px;border-radius:5px;border:1px solid ${({theme})=>theme.border};background:transparent;color:${({theme})=>theme.textMuted};cursor:pointer;display:flex;align-items:center;justify-content:center;&:hover{border-color:${({theme})=>theme.red}55;color:${({theme})=>theme.red};}`;
const AddOcBtn   = styled.button`background:none;border:1px dashed ${({theme})=>theme.border};border-radius:6px;color:${({theme})=>theme.textMuted};font-size:.72rem;padding:.35rem;cursor:pointer;transition:all .15s;&:hover{border-color:${({theme})=>theme.borderLight};color:${({theme})=>theme.text};}`;
const PctSum     = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.67rem;text-align:right;color:${({$ok,theme})=>$ok?theme.green:theme.red};`;

/* ─── Image upload (MarketFormModal) ─────────────────────────────────────── */
const ImgZone    = styled.div`border:2px dashed ${({$has,theme})=>$has?theme.gold+"88":theme.border};border-radius:9px;cursor:pointer;overflow:hidden;transition:border-color .15s;background:${({theme})=>theme.bg};&:hover{border-color:${({theme})=>theme.borderLight};}`;
const ImgPreview = styled.img`width:100%;height:110px;object-fit:cover;display:block;`;
const ImgEmpty   = styled.div`height:72px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.3rem;`;
const ImgActions = styled.div`display:flex;gap:.35rem;padding:.45rem;background:${({theme})=>theme.bgCard};border-top:1px solid ${({theme})=>theme.border};`;
const ImgActBtn  = styled.button`flex:1;font-size:.65rem;padding:.28rem;border-radius:5px;cursor:pointer;border:1px solid ${({theme})=>theme.border};background:transparent;color:${({$del,theme})=>$del?theme.red:theme.textDim};transition:all .15s;&:hover{border-color:${({$del,theme})=>$del?theme.red+"66":theme.borderLight};color:${({$del,theme})=>$del?theme.red:theme.text};}`;

/* ─── Toast ───────────────────────────────────────────────────────────── */
const ToastWrap = styled.div`position:fixed;bottom:1.5rem;right:1.5rem;z-index:9999;display:flex;flex-direction:column;gap:.5rem;`;
const ToastItem = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({$t,theme})=>$t==="ok"?theme.green+"55":$t==="err"?theme.red+"55":theme.borderLight};border-radius:10px;padding:.75rem 1rem;font-size:.78rem;color:${({$t,theme})=>$t==="ok"?theme.green:$t==="err"?theme.red:theme.text};box-shadow:0 6px 24px rgba(0,0,0,.4);min-width:240px;max-width:340px;animation:${fadeUp} .2s ease;`;
const METHOD_META = {
  mobile: { label: "Mobile Money", icon: "📱", color: "#4fc3f7" },
  bank:   { label: "Bank Wire",    icon: "🏦", color: "#ffc107" },
  paypal: { label: "PayPal",       icon: "🅿️", color: "#4dc4ff" },
};

let _tid = 0;
function useToasts() {
  const [list, setList] = useState([]);
  const push = useCallback((msg, t="ok") => {
    const id = ++_tid;
    setList(p => [...p.slice(-3), { id, msg, t }]);
    setTimeout(() => setList(p => p.filter(x => x.id !== id)), 3500);
  }, []);
  return { list, ok: m => push(m,"ok"), err: m => push(m,"err"), info: m => push(m,"info") };
}

function fmt(iso) { if (!iso) return "—"; return new Date(iso).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}); }
function fmtVol(v) { const n=parseFloat(v||0); return n>=1e6?`$${(n/1e6).toFixed(2)}M`:n>=1e3?`$${(n/1e3).toFixed(1)}K`:`$${n.toFixed(0)}`; }
function truncAddr(a="") { return a.length>16?`${a.slice(0,8)}…${a.slice(-5)}`:a; }

// ── Extraire un message lisible depuis une erreur Axios ────────────────────
function extractErr(err, fallback = "Erreur serveur.") {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  if (data.non_field_errors) return data.non_field_errors.join(" ");
  return Object.entries(data)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
    .join(" | ");
}

const ROLES = [
  { id:"viewer",     label:"Viewer",      desc:"Accès lecture seule. Aucun droit de création ou modification." },
  { id:"manager",    label:"Manager",     desc:"Peut créer, modifier et publier des marchés." },
  { id:"moderator",  label:"Moderator",   desc:"Peut gérer les commentaires et signaler du contenu." },
  { id:"support",    label:"Support",     desc:"Gère les tickets utilisateurs, consulte les comptes, répond aux demandes." },
  { id:"finance",    label:"Finance",     desc:"Peut approuver ou rejeter les demandes de retrait." },
  { id:"staff",      label:"Staff",       desc:"Accès complet à la plateforme sauf gestion des rôles." },
  { id:"superadmin", label:"Super Admin", desc:"Accès illimité, y compris la gestion des rôles utilisateurs." },
];
const CATEGORIES_DEFAULT = ["Politics","Crypto","Finance","Sports","Tech","Science","Entertainment","Other"];
const MOCK_STATS       = { total_users:412, total_markets:38, open_markets:11, total_volume:"42800000", pending_withdrawals:5, total_deposits:"6200000" };
const MOCK_USERS       = [
  { id:1, username:"alice",   email:"alice@ex.com",   is_active:true,  role:"manager",  date_joined:"2026-01-10", wallet:{usdc_balance:"1240.00"} },
  { id:2, username:"bob",     email:"bob@ex.com",     is_active:true,  role:"viewer",   date_joined:"2026-01-22", wallet:{usdc_balance:"320.50"} },
  { id:3, username:"charlie", email:"charlie@ex.com", is_active:true,  role:"finance",  date_joined:"2026-02-05", wallet:{usdc_balance:"8900.00"} },
  { id:4, username:"dave",    email:"dave@ex.com",    is_active:false, role:"viewer",   date_joined:"2026-02-18", wallet:{usdc_balance:"0.00"} },
  { id:5, username:"erin",    email:"erin@ex.com",    is_active:true,  role:"moderator",date_joined:"2026-03-01", wallet:{usdc_balance:"450.00"} },
];
const MOCK_MARKETS = [
  { id:1, title:"Will Bitcoin exceed $100K before 2026?",   category_name:"Crypto",   status:"OPEN",     volume:"2430000", created_by:"alice",   created_at:"2026-01-15T10:00:00Z", outcomes:[{id:1,name:"Yes",probability:"0.72"},{id:2,name:"No",probability:"0.28"}] },
  { id:2, title:"US Election 2024 — Democratic wins?",       category_name:"Politics", status:"RESOLVED", volume:"8200000", created_by:"charlie", created_at:"2024-06-01T00:00:00Z", outcomes:[{id:3,name:"Yes",probability:"0.45"},{id:4,name:"No",probability:"0.55"}] },
  { id:3, title:"Will GPT-5 release before June 2026?",      category_name:"Tech",     status:"OPEN",     volume:"560000",  created_by:"alice",   created_at:"2026-02-01T00:00:00Z", outcomes:[{id:5,name:"Yes",probability:"0.61"},{id:6,name:"No",probability:"0.39"}] },
  { id:4, title:"Champions League 2026 — Real Madrid wins?", category_name:"Sports",   status:"DRAFT",    volume:"0",       created_by:"erin",    created_at:"2026-03-10T00:00:00Z", outcomes:[{id:7,name:"Yes",probability:"0.34"},{id:8,name:"No",probability:"0.66"}] },
  { id:5, title:"Will the Fed cut rates in Q1 2026?",        category_name:"Finance",  status:"CLOSED",   volume:"3200000", created_by:"charlie", created_at:"2024-12-01T00:00:00Z", outcomes:[{id:9,name:"Yes",probability:"0.58"},{id:10,name:"No",probability:"0.42"}] },
];
const MOCK_WITHDRAWALS = [
  { id:"a1b2", coin:"USDC", amount:"500.00",  address:"0x3f5CE5…bE", status:"PENDING",  created_at:"2026-03-15T10:00:00Z", user:"alice" },
  { id:"c3d4", coin:"ETH",  amount:"0.85",    address:"0xd8dA6B…45", status:"PENDING",  created_at:"2026-03-14T18:30:00Z", user:"bob" },
  { id:"e5f6", coin:"USDC", amount:"1200.00", address:"0x742d35…72", status:"APPROVED", created_at:"2026-03-13T09:00:00Z", user:"charlie" },
];


const LANGS = [
  { code: "fr", label: "FR", flag: "🇫🇷" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "es", label: "ES", flag: "🇪🇸" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "pt", label: "PT", flag: "🇵🇹" },
  { code: "ar", label: "AR", flag: "🇸🇦" },
];

/* ─── Styled components additionnels ─────────────────────────────────────── */
const LangTabs = styled.div`
  display: flex; gap: .3rem; flex-wrap: wrap;
  padding: .5rem; background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 8px;
`;
const LangTab = styled.button`
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .35rem .65rem; border-radius: 6px;
  border: 1px solid ${({$active, theme}) => $active ? theme.accent : "transparent"};
  background: ${({$active, theme}) => $active ? "rgba(79,195,247,.1)" : "transparent"};
  color: ${({$active, theme}) => $active ? theme.accent : theme.textDim};
  font-family: ${({theme}) => theme.fontMono};
  font-size: .68rem; font-weight: 600; letter-spacing: .04em;
  cursor: pointer; transition: all .15s;
  position: relative;
  &:hover { color: ${({theme}) => theme.text}; }
  ${({$filled, $active , theme}) => $filled && !$active && `
    &::after {
      content: "";
      position: absolute; top: 4px; right: 4px;
      width: 6px; height: 6px;
      background: ${theme.green};
      border-radius: 50%;
    }
  `}
`;
const LangFlag = styled.span`font-size: .9rem; line-height: 1;`;
const TranslateBtn = styled.button`
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .35rem .8rem; border-radius: 6px;
  border: 1px solid ${({theme}) => theme.accent}66;
  background: rgba(79,195,247,.08);
  color: ${({theme}) => theme.accent};
  font-family: ${({theme}) => theme.fontBody};
  font-size: .72rem; font-weight: 600;
  cursor: pointer; transition: all .15s;
  &:hover:not(:disabled) { background: rgba(79,195,247,.15); }
  &:disabled { opacity: .5; cursor: wait; }
`;

const STANDARD_OUTCOME_DEFAULTS = {
  "Oui": { fr:"Oui", en:"Yes", es:"Sí", de:"Ja",   pt:"Sim", ar:"نعم" },
  "Non": { fr:"Non", en:"No",  es:"No", de:"Nein", pt:"Não", ar:"لا"  },
};

/**
 * Retourne true si les traductions d'un outcome correspondent EXACTEMENT
 * aux defaults Oui/Non (ce qui veut dire qu'elles n'ont pas été éditées
 * manuellement par l'admin et peuvent être écrasées).
 */
function isDefaultTranslations(names) {
  for (const frRef of Object.keys(STANDARD_OUTCOME_DEFAULTS)) {
    const defaults = STANDARD_OUTCOME_DEFAULTS[frRef];
    const allMatch = ["en","es","de","pt","ar"].every(
      l => (names[l] || "") === defaults[l]
    );
    if (allMatch) return true;
  }
  return false;
}

/* ─── MarketFormModal v2 ─────────────────────────────────────────────────── */
function MarketFormModal({ market, categories, onClose, onSaved, toast }) {
  const editing = !!market;
  const [activeLang, setActiveLang] = useState("fr");
  const [translating, setTranslating] = useState(false);
  const [liquidity, setLiquidity] = useState(1000);

  // Initialiser les traductions depuis le marché existant
  const initTrans = (key) => {
    const result = {};
    LANGS.forEach(l => {
      const field = `${key}_${l.code}`;
      result[l.code] = market?.[field] || (l.code === "fr" ? market?.[key] || "" : "");
    });
    return result;
  };

  const [titles,       setTitles]       = useState(initTrans("title"));
  const [descriptions, setDescriptions] = useState(initTrans("description"));

  const [form, setForm] = useState({
    category:        String(market?.category || categories[0]?.id || ""),
    close_date:      market?.close_date?.slice(0, 16) || "",
    resolution_date: market?.resolution_date?.slice(0, 16) || "",
    display_priority: market?.display_priority ?? 0,
  });

  const API = process.env.REACT_APP_API_URL || "";
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(market?.image_url ? API + market.image_url : null);
  const imageRef = useRef(null);

  // Outcomes avec traductions
  const [outcomes, setOutcomes] = useState(() => {
    if (market?.outcomes) {
      return market.outcomes.map(o => {
        const names = {};
        LANGS.forEach(l => {
          names[l.code] = o.names_i18n?.[l.code] || (l.code === "fr" ? o.name : "");
        });
        return { names, probability: String(Math.round(parseFloat(o.probability) * 100)) };
      });
    }
    return [
      { names: {fr:"Oui", en:"Yes", es:"Sí", de:"Ja", pt:"Sim", ar:"نعم"}, probability:"65" },
      { names: {fr:"Non", en:"No",  es:"No", de:"Nein", pt:"Não", ar:"لا"}, probability:"35" },
    ];
  });

  const [loading,  setLoading]  = useState(false);
  const [apiError, setApiError] = useState("");
// ── Auto-traduction parallélisée ────────────────────────────────────────
  const [translateProgress, setTranslateProgress] = useState({ done: 0, total: 0 });
  const [preserveManual,    setPreserveManual]    = useState(true);
// ── Auto-traduction au changement d'onglet ─────────────────────────────
  // Quand on bascule sur une langue dont le titre/description est vide,
  // on traduit automatiquement depuis le FR (silencieusement).
  const [autoTranslating, setAutoTranslating] = useState(false);

  useEffect(() => {
    // Pas sur FR (langue source)
    if (activeLang === "fr") return;

    // Besoin du FR comme source
    const sourceTitle = titles.fr?.trim();
    const sourceDesc  = descriptions.fr?.trim();
    if (!sourceTitle && !sourceDesc) return;

    // Ne rien faire si la langue cible est déjà remplie (les 2 champs)
    const titleEmpty = !titles[activeLang]?.trim();
    const descEmpty  = !descriptions[activeLang]?.trim();
    if (!titleEmpty && !descEmpty) return;

    let cancelled = false;

    const autoTranslate = async () => {
      setAutoTranslating(true);
      try {
        // Traduire le titre si vide
        if (sourceTitle && titleEmpty) {
          const r = await adminAutoTranslate(sourceTitle, "fr");
          if (cancelled) return;
          const translated = r.data?.[activeLang];
          if (translated) {
            setTitles(t => {
              // Re-check : si l'user a tapé quelque chose entre temps, on respecte
              if (t[activeLang]?.trim()) return t;
              return { ...t, [activeLang]: translated };
            });
          }
        }

        // Traduire la description si vide ET si on a une description FR
        if (sourceDesc && descEmpty) {
          const r = await adminAutoTranslate(sourceDesc, "fr");
          if (cancelled) return;
          const translated = r.data?.[activeLang];
          if (translated) {
            setDescriptions(d => {
              if (d[activeLang]?.trim()) return d;
              return { ...d, [activeLang]: translated };
            });
          }
        }
      } catch (err) {
        // Silencieux — pas de toast d'erreur (ne pas perturber l'UX)
        console.warn("Auto-translate on tab switch failed:", err);
      } finally {
        if (!cancelled) setAutoTranslating(false);
      }
    };

    autoTranslate();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLang]);

  // Traduire UN seul champ vers la langue active
  const translateField = async (kind) => {
    const sourceFr =
      kind === "title"       ? titles.fr.trim() :
      kind === "description" ? descriptions.fr.trim() :
                               "";
    if (!sourceFr) {
      toast.err("Saisis d'abord la version française.");
      return;
    }
    if (activeLang === "fr") {
      toast.err("Tu es déjà sur le français.");
      return;
    }
    setTranslating(true);
    try {
      const r = await adminAutoTranslate(sourceFr, "fr");
      const translated = r.data[activeLang];
      if (!translated) {
        toast.err("Pas de traduction reçue pour cette langue.");
        return;
      }
      if (kind === "title") {
        setTitles(t => ({ ...t, [activeLang]: translated }));
      } else if (kind === "description") {
        setDescriptions(d => ({ ...d, [activeLang]: translated }));
      }
      toast.ok(`✓ Traduit en ${activeLang.toUpperCase()}`);
    } catch (err) {
      toast.err("Erreur : " + (err.response?.data?.detail || err.message));
    } finally {
      setTranslating(false);
    }
  };
  const handleAutoTranslate = async () => {
    const titleFr = titles.fr.trim();
    if (!titleFr) {
      toast.err("Saisis d'abord le titre en français.");
      return;
    }

    // Construire la liste des tâches de traduction
    const tasks = [];

    // Tâche : titre
    tasks.push({
      kind:    "title",
      source:  titleFr,
      // Ce qui est considéré comme "manuellement rempli" (à préserver)
      skipIf:  () => false,   // titre : toujours traduit
    });

    // Tâche : description (si présente)
    if (descriptions.fr.trim()) {
      tasks.push({
        kind:    "description",
        source:  descriptions.fr.trim(),
        skipIf:  () => false,
      });
    }

    // Tâches : un par outcome avec un nom FR
    outcomes.forEach((oc, i) => {
      if (oc.names.fr.trim()) {
        tasks.push({
          kind:    "outcome",
          index:   i,
          source:  oc.names.fr.trim(),
          skipIf:  () => false,
        });
      }
    });

    if (tasks.length === 0) {
      toast.err("Rien à traduire.");
      return;
    }

    setTranslating(true);
    setTranslateProgress({ done: 0, total: tasks.length });

    // Lancer TOUTES les requêtes en parallèle
    const results = await Promise.allSettled(
      tasks.map(async (task) => {
        const r = await adminAutoTranslate(task.source, "fr");
        setTranslateProgress(p => ({ ...p, done: p.done + 1 }));
        return { task, data: r.data };
      })
    );

    // Appliquer les résultats
    let successCount = 0;
    let errorCount   = 0;

    results.forEach((result) => {
      if (result.status === "rejected") {
        errorCount++;
        return;
      }
      successCount++;
      const { task, data } = result.value;

      if (task.kind === "title") {
        setTitles(t => {
          const next = { ...t };
          LANGS.forEach(l => {
            if (l.code === "fr") return;
            // Préserver la traduction manuelle si activé
            if (preserveManual && t[l.code]?.trim()) return;
            if (data[l.code]) next[l.code] = data[l.code];
          });
          return next;
        });
      }
      else if (task.kind === "description") {
        setDescriptions(d => {
          const next = { ...d };
          LANGS.forEach(l => {
            if (l.code === "fr") return;
            if (preserveManual && d[l.code]?.trim()) return;
            if (data[l.code]) next[l.code] = data[l.code];
          });
          return next;
        });
      }
        else if (task.kind === "outcome") {
          setOutcomes(ocs => ocs.map((oc, j) => {
            if (j !== task.index) return oc;
        
            // Si les traductions actuelles sont les defaults Yes/No, on les écrase
            // TOUJOURS, même avec preserveManual=true (sinon le bug "Yes/No persistant")
            const hasDefaults = isDefaultTranslations(oc.names);
        
            const newNames = { ...oc.names };
            LANGS.forEach(l => {
              if (l.code === "fr") return;
              // preserveManual SAUF si les valeurs actuelles sont les defaults
              if (preserveManual && !hasDefaults && oc.names[l.code]?.trim()) return;
              if (data[l.code]) newNames[l.code] = data[l.code];
            });
            return { ...oc, names: newNames };
          }));
        }
    });

    setTranslating(false);
    setTranslateProgress({ done: 0, total: 0 });

    if (errorCount === 0) {
      toast.ok(`✓ ${successCount} traductions terminées — vérifie chaque langue.`);
    } else if (successCount > 0) {
      toast.err(`${successCount} OK, ${errorCount} échoués. Réessaie pour les manqués.`);
    } else {
      toast.err("Échec de la traduction. Vérifie /admin/translate/diag/");
    }
  };

 
const setOc = (i, key, val, lang = null) => {
  setOutcomes(ocs => ocs.map((o, j) => {
    if (j !== i) return o;
 
    if (lang) {
      // Cas 1 : on modifie le FR
      if (lang === "fr") {
        // Si les autres langues étaient les defaults Yes/No/Sí... → clear
        // (l'admin tape un nom custom, les traductions Yes/No deviennent obsolètes)
        if (isDefaultTranslations(o.names)) {
          return {
            ...o,
            names: { fr: val, en: "", es: "", de: "", pt: "", ar: "" },
          };
        }
      }
      // Cas standard : update uniquement la langue concernée
      return { ...o, names: { ...o.names, [lang]: val } };
    }
 
    return { ...o, [key]: val };
  }));
};
 

  const pctSum = outcomes.reduce((s, o) => s + (parseFloat(o.probability) || 0), 0);
  const pctOk  = Math.abs(pctSum - 100) < 0.5;

  const blockReasons = [
    !titles.fr.trim()      && "Titre français requis",
    outcomes.length < 2    && "Minimum 2 outcomes",
    !pctOk                 && `Somme = ${pctSum.toFixed(1)}% (doit être 100%)`,
    outcomes.some(o => !o.names.fr.trim()) && "Tous les outcomes doivent avoir un nom français",
  ].filter(Boolean);

  const canSave = blockReasons.length === 0 && !loading;

  // Combien de langues remplies par champ
  const filledCount = (obj) => LANGS.filter(l => obj[l.code]?.trim()).length;
  const titleFilledCount = filledCount(titles);
  const descFilledCount  = filledCount(descriptions);


const save = async () => {
  if (!canSave) return;
  setApiError("");
  setLoading(true);
 
  try {
    // ── Détection des outcomes qui ont besoin d'une auto-traduction ──
    // 2 cas déclenchent une re-traduction :
    //   (a) FR rempli mais une langue est vide
    //   (b) Les traductions matchent les defaults Yes/No mais le FR n'est PAS Oui/Non
    const needsTranslate = outcomes
      .map((o, i) => ({ o, i }))
      .filter(({ o }) => {
        const fr = (o.names.fr || "").trim();
        if (!fr) return false;
 
        // Cas (a) — au moins une langue vide
        const hasEmpty = ["en","es","de","pt","ar"].some(
          l => !(o.names[l] || "").trim()
        );
        if (hasEmpty) return true;
 
        // Cas (b) — defaults stale (FR custom mais autres langues = Yes/No)
        if (isDefaultTranslations(o.names) && !["Oui","Non"].includes(fr)) {
          return true;
        }
 
        return false;
      });
 
    let workingOutcomes = outcomes;
 
    if (needsTranslate.length > 0) {
      toast.ok(`Traduction de ${needsTranslate.length} outcome(s)…`);
 
      const updates = await Promise.all(
        needsTranslate.map(async ({ o, i }) => {
          try {
            const r = await adminAutoTranslate(o.names.fr, "fr");
            return {
              i,
              names: {
                fr: o.names.fr,
                en: r.data.en || o.names.fr,
                es: r.data.es || o.names.fr,
                de: r.data.de || o.names.fr,
                pt: r.data.pt || o.names.fr,
                ar: r.data.ar || o.names.fr,
              },
            };
          } catch (err) {
            // Si l'API échoue : fallback sur le FR pour les langues vides
            const filled = { ...o.names };
            ["en","es","de","pt","ar"].forEach(l => {
              if (!(filled[l] || "").trim()) filled[l] = o.names.fr;
            });
            return { i, names: filled };
          }
        })
      );
 
      workingOutcomes = outcomes.map((o, idx) => {
        const upd = updates.find(u => u.i === idx);
        return upd ? { ...o, names: upd.names } : o;
      });
 
      setOutcomes(workingOutcomes);  // refléter dans l'UI
    }
 
    // ── À PARTIR D'ICI : code existant, mais utilise workingOutcomes ──
    const outcomesPayload = workingOutcomes.map(o => {
      const data = { probability: (parseFloat(o.probability) / 100).toFixed(4) };
      LANGS.forEach(l => { data[`name_${l.code}`] = o.names[l.code] || ""; });
      return data;
    });
 
    const payload = {
      category:        parseInt(form.category, 10),
      close_date:      form.close_date || undefined,
      resolution_date: form.resolution_date || undefined,
      outcomes:        outcomesPayload,
      initial_liquidity: liquidity,
      display_priority: parseInt(form.display_priority, 10) || 0,
    };
 
    LANGS.forEach(l => {
      payload[`title_${l.code}`]       = titles[l.code] || "";
      payload[`description_${l.code}`] = descriptions[l.code] || "";
    });
 
    let res;
    if (imageFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (k === "outcomes") fd.append(k, JSON.stringify(v));
        else if (v !== undefined) fd.append(k, v);
      });
      fd.append("image_url", imageFile);
      res = editing
        ? await adminUpdateMarket(market.id, fd)
        : await adminCreateMarket(fd);
    } else {
      res = editing
        ? await adminUpdateMarket(market.id, payload)
        : await adminCreateMarket(payload);
    }
 
    toast.ok(editing ? "Market mis à jour !" : "Market créé !");
    onSaved();
  } catch (err) {
    const data = err?.response?.data;
    let msg = "Erreur serveur.";
    if (data) {
      if (typeof data === "string") msg = data;
      else if (data.detail) msg = data.detail;
      else msg = Object.entries(data)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
        .join(" | ");
    }
    setApiError(msg);
    toast.err(msg);
  } finally {
    setLoading(false);
  }
};

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox $w="640px">
        <MHead>
          <MTitle>{editing ? "Edit Market" : "Create New Market"}</MTitle>
          <MClose onClick={onClose}>✕</MClose>
        </MHead>

        <MBody>
          {/* ── Sélecteur de langue ── */}
{/* ── Sélecteur de langue + auto-traduction ── */}
          <Field>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              flexWrap:"wrap",gap:".5rem"}}>
              <Label>Langue d'édition</Label>
              <div style={{display:"flex",alignItems:"center",gap:".6rem"}}>
                {/* Toggle préserver manuel */}
                <label style={{display:"inline-flex",alignItems:"center",gap:".35rem",
                  fontSize:".65rem",color:"#8a9aaa",cursor:"pointer",userSelect:"none"}}>
                  <input
                    type="checkbox"
                    checked={preserveManual}
                    onChange={e => setPreserveManual(e.target.checked)}
                    style={{cursor:"pointer"}}
                  />
                  Garder mes traductions
                </label>
                <TranslateBtn
                  type="button"
                  onClick={handleAutoTranslate}
                  disabled={translating || !titles.fr.trim()}
                >
                  {translating
                    ? `Traduction ${translateProgress.done}/${translateProgress.total}…`
                    : "🪄 Auto-traduire depuis FR"}
                </TranslateBtn>
              </div>
            </div>
<LangTabs>
              {LANGS.map(l => (
                <LangTab
                  key={l.code}
                  type="button"
                  $active={activeLang === l.code}
                  $filled={!!titles[l.code]?.trim()}
                  onClick={() => setActiveLang(l.code)}
                >
                  <LangFlag>{l.flag}</LangFlag>
                  {l.label}
                  {/* Indicateur loading SUR la langue active si auto-traduction en cours */}
                  {autoTranslating && activeLang === l.code && (
                    <span style={{
                      display:"inline-block",
                      width:"6px",
                      height:"6px",
                      borderRadius:"50%",
                      background:"#4fc3f7",
                      marginLeft:".25rem",
                      animation:"pulse 1s ease infinite",
                    }}/>
                  )}
                  {/* Check vert si rempli */}
                  {!!titles[l.code]?.trim() && !(autoTranslating && activeLang === l.code) && (
                    <span style={{
                      fontSize:".55rem",
                      color: activeLang === l.code ? "#4fc3f7" : "#00e676",
                      marginLeft:".1rem",
                    }}>
                      ✓
                    </span>
                  )}
                </LangTab>
              ))}
            </LangTabs>
            {/* Barre de progression pendant la traduction */}
            {translating && translateProgress.total > 0 && (
              <div style={{
                marginTop:".4rem",
                height:"3px",
                background:"#1e2530",
                borderRadius:"100px",
                overflow:"hidden",
              }}>
                <div style={{
                  height:"100%",
                  width: `${(translateProgress.done / translateProgress.total) * 100}%`,
                  background:"linear-gradient(90deg,#4fc3f7,#00e676)",
                  transition:"width .3s",
                }}/>
              </div>
            )}

            <span style={{fontSize:".65rem",color:"#5a6a7a"}}>
              {titleFilledCount}/{LANGS.length} langues remplies
              {preserveManual && titleFilledCount > 1 && (
                <span style={{color:"#4fc3f7",marginLeft:".4rem"}}>
                  · Mes traductions seront préservées
                </span>
              )}
            </span>
          </Field>

{/* ── Titre (langue active) ── */}
          <Field>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <Label>
                Question ({LANGS.find(l => l.code === activeLang)?.label}){" "}
                {activeLang === "fr" && <Required>*</Required>}
              </Label>
              {activeLang !== "fr" && titles.fr.trim() && (
                <InlineTranslate
                  type="button"
                  onClick={() => translateField("title")}
                  disabled={translating}
                >
                  🪄 Traduire FR → {activeLang.toUpperCase()}
                </InlineTranslate>
              )}
            </div>
<div style={{position:"relative"}}>
              <Input
                dir={activeLang === "ar" ? "rtl" : "ltr"}
                placeholder={
                  activeLang === "fr"
                    ? "Le Bitcoin dépassera-t-il 100K$ avant fin 2026 ?"
                    : autoTranslating
                      ? "Traduction automatique en cours…"
                      : `Saisissez en ${activeLang.toUpperCase()} ou cliquez Auto-traduire`
                }
                value={titles[activeLang] || ""}
                onChange={e => setTitles(t => ({ ...t, [activeLang]: e.target.value }))}
                maxLength={255}
                style={autoTranslating && !titles[activeLang]?.trim() ? {opacity:.6} : {}}
              />
              {autoTranslating && !titles[activeLang]?.trim() && (
                <span style={{
                  position:"absolute",
                  right:".7rem",
                  top:"50%",
                  transform:"translateY(-50%)",
                  fontSize:".65rem",
                  color:"#4fc3f7",
                  fontFamily:"DM Mono,monospace",
                  pointerEvents:"none",
                }}>
                  🪄 …
                </span>
              )}
            </div>
          </Field>

{/* ── Description (langue active) ── */}
          <Field>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <Label>Description ({LANGS.find(l => l.code === activeLang)?.label})</Label>
              {activeLang !== "fr" && descriptions.fr.trim() && (
                <InlineTranslate
                  type="button"
                  onClick={() => translateField("description")}
                  disabled={translating}
                >
                  🪄 Traduire FR → {activeLang.toUpperCase()}
                </InlineTranslate>
              )}
            </div>
<Tarea
              dir={activeLang === "ar" ? "rtl" : "ltr"}
              placeholder={
                activeLang === "fr"
                  ? "Critères de résolution…"
                  : autoTranslating
                    ? "Traduction automatique en cours…"
                    : ""
              }
              value={descriptions[activeLang] || ""}
              onChange={e => setDescriptions(d => ({ ...d, [activeLang]: e.target.value }))}
              style={autoTranslating && !descriptions[activeLang]?.trim() ? {opacity:.6} : {}}
            />
          </Field>

          {/* ── Catégorie + Image ── */}
          <Row2>
            <Field>
              <Label>Catégorie <Required>*</Required></Label>
              <Select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Image</Label>
              <ImgZone $has={!!imagePreview} onClick={() => !imagePreview && imageRef.current?.click()}>
                {imagePreview ? (
                  <>
                    <ImgPreview src={imagePreview} alt="preview" onError={() => setImagePreview(null)}/>
                    <ImgActions>
                      <ImgActBtn type="button" onClick={e => { e.stopPropagation(); imageRef.current?.click(); }}>
                        🔄 Changer
                      </ImgActBtn>
                      <ImgActBtn $del type="button" onClick={e => { e.stopPropagation(); setImageFile(null); setImagePreview(null); }}>
                        🗑 Retirer
                      </ImgActBtn>
                    </ImgActions>
                  </>
                ) : (
                  <ImgEmpty>
                    <span style={{fontSize:"1.3rem"}}>🖼</span>
                    <span style={{fontSize:".68rem",color:"#5a6a7a"}}>Cliquer pour importer</span>
                  </ImgEmpty>
                )}
              </ImgZone>
              <input ref={imageRef} type="file" accept="image/*"
                style={{display:"none"}}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (imagePreview && imagePreview.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
                  setImageFile(f);
                  setImagePreview(URL.createObjectURL(f));
                  e.target.value = "";
                }}
              />
            </Field>
          </Row2>

          {/* ── Dates ── */}
          <Row2>
            <Field>
              <Label>Date de clôture</Label>
              <Input type="datetime-local" value={form.close_date}
                onChange={e => setForm(f => ({...f, close_date: e.target.value}))}/>
            </Field>
            <Field>
              <Label>Date de résolution</Label>
              <Input type="datetime-local" value={form.resolution_date}
                onChange={e => setForm(f => ({...f, resolution_date: e.target.value}))}/>
            </Field>
          </Row2>

          {/* ── Priorité d'affichage ── */}
          <Row2>
            <Field>
              <Label>Priorité d'affichage</Label>
              <Input type="number" step="1" value={form.display_priority}
                onChange={e => setForm(f => ({...f, display_priority: e.target.value}))}/>
              <span style={{fontSize:".68rem",color:"#5a6a7a"}}>Plus la valeur est élevée, plus le marché remonte en tête des listes. 0 = pas de mise en avant.</span>
            </Field>
          </Row2>

          {/* ── Outcomes ── */}
          <Field>
            <Label>Outcomes (langue {LANGS.find(l => l.code === activeLang)?.label}) — somme = 100%</Label>
            <OcBlock>
              <div style={{display:"grid",gridTemplateColumns:"1fr 72px 28px",gap:".4rem",
                paddingBottom:".3rem",borderBottom:"1px solid #1e2530"}}>
                <span style={{fontSize:".65rem",color:"#5a6a7a"}}>Nom ({activeLang})</span>
                <span style={{fontSize:".65rem",color:"#5a6a7a",textAlign:"center"}}>%</span>
                <span/>
              </div>
              {outcomes.map((o, i) => (
                <OcRow key={i}>
                  <OcInput
                    dir={activeLang === "ar" ? "rtl" : "ltr"}
                    value={o.names[activeLang] || ""}
                    onChange={e => setOc(i, null, e.target.value, activeLang)}
                    placeholder={`Option ${i + 1}`}
                  />
                  <OcPct type="number" min="0" max="100" step="1" value={o.probability}
                    onChange={e => setOc(i, "probability", e.target.value)}/>
                  <OcDel type="button" onClick={() => setOutcomes(ocs => ocs.filter((_, j) => j !== i))}
                    disabled={outcomes.length <= 2}>✕</OcDel>
                </OcRow>
              ))}
              <button type="button"
                onClick={() => setOutcomes(ocs => [...ocs, {
                  names: LANGS.reduce((acc, l) => ({...acc, [l.code]: ""}), {}),
                  probability: "0",
                }])}
                style={{background:"none",border:"1px dashed #1e2530",borderRadius:"6px",
                  color:"#5a6a7a",fontSize:".72rem",padding:".35rem",cursor:"pointer"}}>
                + Add outcome
              </button>
              <PctSum $ok={pctOk}>
                Somme : {pctSum.toFixed(1)}% {!pctOk && "— doit être égal à 100%"}
              </PctSum>
            </OcBlock>
            <span style={{fontSize:".65rem",color:"#5a6a7a"}}>
              💡 Change de langue en haut pour traduire les outcomes
            </span>
          </Field>

          {/* ── Liquidité (uniquement à la création) ── */}
          {!market && (
            <Field>
              <Label>Liquidité initiale (EVP) <Required>*</Required></Label>
              <LiqPresets>
                {[
                  { value: 100,   label: "Faible",     hint: "Marché expérimental" },
                  { value: 1000,  label: "Standard",   hint: "Recommandé" },
                  { value: 5000,  label: "Élevée",     hint: "Gros volumes" },
                  { value: 10000, label: "Très élevée",hint: "Polymarket-grade" },
                ].map(p => (
                  <LiqPreset key={p.value} type="button"
                    $active={liquidity === p.value}
                    onClick={() => setLiquidity(p.value)}>
                    <LiqPresetLabel $active={liquidity === p.value}>{p.label}</LiqPresetLabel>
                    <LiqPresetValue $active={liquidity === p.value}>
                      {p.value.toLocaleString()} EVP
                    </LiqPresetValue>
                    <LiqPresetHint>{p.hint}</LiqPresetHint>
                  </LiqPreset>
                ))}
              </LiqPresets>
            </Field>
          )}

          {/* Raisons blocage */}
          {blockReasons.length > 0 && (
            <div style={{fontSize:".7rem",color:"#ffc107",background:"rgba(255,193,7,.06)",
              border:"1px solid #ffc10733",borderRadius:"7px",padding:".6rem .8rem",
              display:"flex",flexDirection:"column",gap:".2rem"}}>
              {blockReasons.map((r, i) => <span key={i}>⚠ {r}</span>)}
            </div>
          )}

          {apiError && (
            <div style={{fontSize:".75rem",color:"#ff3b5c",background:"#ff3b5c11",
              border:"1px solid #ff3b5c33",borderRadius:"7px",padding:".65rem .8rem"}}>
              ✕ {apiError}
            </div>
          )}
        </MBody>

        <MFoot>
          <CancelBtn2 onClick={onClose}>Cancel</CancelBtn2>
          <SaveBtn disabled={!canSave} onClick={save}>
            {loading ? "Saving…" : editing ? "Save Changes" : "Create Market"}
          </SaveBtn>
        </MFoot>
      </ModalBox>
    </Overlay>
  );
}


function ResolveModal({ market, onClose, onResolved, toast }) {
  const [selected, setSelected] = useState(market.outcomes?.[0]?.id || null);
  const [reason,   setReason]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const handle = async () => {
    setLoading(true);
    try {
      await adminResolveMarket(market.id, { winning_outcome_id: selected, notes: reason });
      toast.ok("Marché résolu.");
      onResolved();
    } catch (err) {
      toast.err(extractErr(err, "Échec de la résolution."));
    } finally {
      setLoading(false);
    }
  };
  return (<Overlay onClick={e=>e.target===e.currentTarget&&onClose()}><ModalBox $w="380px"><MHead><MTitle>Resolve Market</MTitle><MClose onClick={onClose}>✕</MClose></MHead><MBody><p style={{fontSize:".8rem",color:"#8a9aaa",lineHeight:1.5}}>{market.title}</p><Field><Label>Winning Outcome *</Label>{(market.outcomes||[]).map(o=><div key={o.id} onClick={()=>setSelected(o.id)} style={{display:"flex",alignItems:"center",gap:".6rem",padding:".6rem .8rem",borderRadius:"7px",border:`1px solid ${selected===o.id?"#00e67688":"#1e2530"}`,background:selected===o.id?"#00e67211":"transparent",cursor:"pointer",marginBottom:".35rem"}}><span style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${selected===o.id?"#00e676":"#2a3340"}`,background:selected===o.id?"#00e676":"transparent",flexShrink:0}}/><span style={{fontSize:".82rem",fontWeight:500}}>{o.name}</span></div>)}</Field><Field><Label>Notes</Label><Tarea placeholder="Add source…" value={reason} onChange={e=>setReason(e.target.value)} style={{minHeight:"56px"}}/></Field><div style={{fontSize:".7rem",color:"#ff3b5c",background:"#ff3b5c11",border:"1px solid #ff3b5c22",borderRadius:"7px",padding:".6rem .8rem"}}>⚠ Irréversible.</div></MBody><MFoot><CancelBtn2 onClick={onClose}>Cancel</CancelBtn2><SaveBtn disabled={!selected||loading} onClick={handle} style={{background:!selected||loading?"#141820":"#00e676"}}>{loading?"Resolving…":"Confirm"}</SaveBtn></MFoot></ModalBox></Overlay>);
}

function UserRoleModal({ user: u, onClose, onSaved, toast }) {
  const [role,    setRole]    = useState(u.role || "viewer");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try {
      await adminSetRole(u.id, { role });
      toast.ok(`Rôle mis à jour : ${role}.`);
      onSaved();
    } catch (err) {
      toast.err(extractErr(err, "Impossible de changer le rôle."));
    } finally {
      setLoading(false);
    }
  };
  return (<Overlay onClick={e=>e.target===e.currentTarget&&onClose()}><ModalBox $w="400px"><MHead><MTitle>Edit Role — {u.username}</MTitle><MClose onClick={onClose}>✕</MClose></MHead><MBody><p style={{fontSize:".78rem",color:"#8a9aaa"}}>Assign a role that controls what actions this user can perform.</p><div style={{display:"flex",flexDirection:"column",gap:".4rem"}}>{ROLES.map(r=><div key={r.id} onClick={()=>setRole(r.id)} style={{display:"flex",alignItems:"flex-start",gap:".7rem",padding:".7rem .9rem",borderRadius:"8px",border:`1px solid ${role===r.id?"#ffc10788":"#1e2530"}`,background:role===r.id?"rgba(255,193,7,.06)":"transparent",cursor:"pointer"}}><span style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${role===r.id?"#ffc107":"#2a3340"}`,background:role===r.id?"#ffc107":"transparent",flexShrink:0,marginTop:"2px"}}/><div><div style={{fontSize:".8rem",fontWeight:600,color:role===r.id?"#ffc107":"#e8edf2"}}>{r.label}</div><div style={{fontSize:".68rem",color:"#5a6a7a",marginTop:".1rem"}}>{r.desc}</div></div></div>)}</div></MBody><MFoot><CancelBtn2 onClick={onClose}>Cancel</CancelBtn2><SaveBtn disabled={loading} onClick={save}>{loading?"Saving…":"Apply Role"}</SaveBtn></MFoot></ModalBox></Overlay>);
}

function RejectModal({ wd, onClose, onRejected, toast }) {
  const [reason,  setReason]  = useState("");
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try {
      await adminRejectWithdrawal(wd.id, { reason });
      toast.err("Retrait rejeté.");
      onRejected();
    } catch (err) {
      toast.err(extractErr(err, "Impossible de rejeter ce retrait."));
    } finally {
      setLoading(false);
    }
  };
  return (<Overlay onClick={e=>e.target===e.currentTarget&&onClose()}><ModalBox $w="360px"><MHead><MTitle>Reject Withdrawal</MTitle><MClose onClick={onClose}>✕</MClose></MHead><MBody><p style={{fontSize:".78rem",color:"#8a9aaa"}}>Withdrawal of <strong style={{color:"#e8edf2"}}>${parseFloat(wd.amount).toFixed(2)} {wd.coin}</strong> by <strong style={{color:"#e8edf2"}}>{wd.user}</strong></p><Field><Label>Reason *</Label><Tarea placeholder="Explain why…" value={reason} onChange={e=>setReason(e.target.value)} style={{minHeight:"64px"}}/></Field></MBody><MFoot><CancelBtn2 onClick={onClose}>Cancel</CancelBtn2><SaveBtn disabled={!reason.trim()||loading} onClick={handle} style={{background:!reason.trim()||loading?"#141820":"#ff3b5c",color:"#fff"}}>{loading?"Rejecting…":"Confirm Rejection"}</SaveBtn></MFoot></ModalBox></Overlay>);
}

/* ─── Tabs ────────────────────────────────────────────────────────────────── */
function OverviewTab({ stats, pendingWd, setTab }) {
  const s = stats || MOCK_STATS;
  return (
    <>
      <StatGrid>
        {[{label:"Total Volume",val:fmtVol(s.total_volume),c:"text"},{label:"Total Deposits",val:fmtVol(s.total_deposits),c:"green"},{label:"Open Markets",val:s.open_markets,c:"accent"},{label:"All Markets",val:s.total_markets,c:"text"},{label:"Registered Users",val:s.total_users,c:"text"},{label:"Pending Withdrawals",val:s.pending_withdrawals,c:s.pending_withdrawals>0?"gold":"text"}].map((x,i)=>(
          <StatCard key={x.label} $i={i}><StatVal $c={x.c}>{x.val}</StatVal><StatLbl>{x.label}</StatLbl></StatCard>
        ))}
      </StatGrid>
      {pendingWd > 0 && <Panel><PanelHead><PanelTitle>⚠ {pendingWd} pending withdrawal{pendingWd>1?"s":""} require your review</PanelTitle><Btn $t="warn" onClick={()=>setTab("withdrawals")}>Review →</Btn></PanelHead></Panel>}
      <Panel><PanelHead><PanelTitle>Role Permissions Matrix</PanelTitle></PanelHead>
        <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:".72rem",fontFamily:"DM Mono,monospace"}}><thead><tr>{["Permission","Viewer","Manager","Moderator","Support","Finance","Staff","Super Admin"].map(h=><th key={h} style={{padding:".5rem .75rem",textAlign:"left",color:"#5a6a7a",fontWeight:500,borderBottom:"1px solid #1e2530"}}>{h}</th>)}</tr></thead><tbody>{[
          ["View markets",         true, true, true, true, true, true, true],
          ["Create markets",       false,true, false,false,false,true, true],
          ["Edit markets",         false,true, false,false,false,true, true],
          ["Publish markets",      false,true, false,false,false,true, true],
          ["Resolve markets",      false,false,false,false,false,true, true],
          ["Manage comments",      false,false,true, false,false,true, true],
          ["View users",           false,false,false,true, true, true, true],
          ["Manage tickets",       false,false,false,true, false,true, true],
          ["Approve withdrawals",  false,false,false,false,true, true, true],
          ["Manage users",         false,false,false,false,false,true, true],
          ["Assign roles",         false,false,false,false,false,false,true],
        ].map(([perm,...checks])=><tr key={perm} style={{borderBottom:"1px solid #1e2530"}}><td style={{padding:".5rem .75rem",color:"#8a9aaa"}}>{perm}</td>{checks.map((ok,ci)=><td key={ci} style={{padding:".5rem .75rem",textAlign:"left",color:ok?"#00e676":"#2a3340"}}>{ok?"✓":"—"}</td>)}</tr>)}</tbody></table></div>
      </Panel>
    </>
  );
}

function CountriesTab({ toast }) {
  const [range, setRange]     = useState("all");
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminGetUXByCountry(range);
      setData(r.data);
    } catch (err) {
      toast.err(extractErr(err, "Impossible de charger les statistiques par pays."));
      setData({ countries: [], total_users: 0, total_volume: 0, total_trades: 0, total_open_sessions: 0, country_count: 0 });
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const countries = data?.countries || [];
  const maxUsers  = Math.max(...countries.map(c => c.users_count), 1);
  const COLS = "40px 2fr 110px 130px 110px 110px";
  const MIN  = "660px";

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>Users by Country{data ? ` (${data.country_count})` : ""}</PanelTitle>
        <PanelActions>
          {[["7d","7j"],["30d","30j"],["90d","90j"],["all","Tout"]].map(([val,label]) =>
            <FilterChip key={val} $active={range===val} onClick={()=>setRange(val)}>{label}</FilterChip>
          )}
        </PanelActions>
      </PanelHead>

      {!loading && data && (
        <div style={{padding:".75rem 1.25rem",display:"flex",gap:"1.5rem",flexWrap:"wrap",fontSize:".78rem",color:"#8a9aaa",borderBottom:"1px solid #1e2530"}}>
          <span><strong style={{color:"#e8edf2"}}>{data.country_count}</strong> pays</span>
          <span><strong style={{color:"#e8edf2"}}>{data.total_users.toLocaleString()}</strong> inscrits</span>
          <span>Volume total&nbsp;: <strong style={{color:"#00e676"}}>{fmtVol(data.total_volume)}</strong></span>
          <span><strong style={{color:"#e8edf2"}}>{data.total_trades.toLocaleString()}</strong> trades</span>
          <span><strong style={{color:"#4fc3f7"}}>{(data.total_open_sessions||0).toLocaleString()}</strong> sessions ouvertes</span>
        </div>
      )}

      <TableScroll>
        <THead $cols={COLS} $minW={MIN}><TH></TH><TH>Country</TH><TH>Users</TH><TH>Volume</TH><TH>Trades</TH><TH>Sessions</TH></THead>
        {loading ? Array.from({length:5}).map((_,i)=><Skel key={i}/>) :
          countries.length===0 ? <Empty>Aucune donnée pour cette période.</Empty> :
          countries.map(c => (
            <TRow key={c.code} $cols={COLS} $minW={MIN}>
              <Cell style={{fontSize:"1.1rem"}}>{c.flag}</Cell>
              <Cell>
                {c.name}
                <div style={{height:"4px",borderRadius:"2px",background:"#1e2530",marginTop:".3rem",overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${(c.users_count/maxUsers)*100}%`,background:"#00e676",borderRadius:"2px"}}/>
                </div>
              </Cell>
              <Cell $mono>{c.users_count.toLocaleString()}</Cell>
              <Cell $mono>{fmtVol(c.total_volume)}</Cell>
              <Cell $mono>{c.trades_count.toLocaleString()}</Cell>
              <Cell $mono style={{color:(c.open_sessions||0)>0?"#4fc3f7":undefined}}>{(c.open_sessions||0).toLocaleString()}</Cell>
            </TRow>
          ))
        }
      </TableScroll>
    </Panel>
  );
}

function MarketsTab({ categories, toast }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);const[filter,setFilter]=useState("ALL");const[search,setSearch]=useState("");const[modal,setModal]=useState(null);const[acting,setActing]=useState(null);
  const [autoPriority,setAutoPriority]=useState(null);const[autoPriorityBusy,setAutoPriorityBusy]=useState(false);
  const load=useCallback(async()=>{
    setLoading(true);
    try{ const r=await adminGetMarkets(); setItems(r.data.results||r.data); }
    catch(err){ toast.err(extractErr(err,"Impossible de charger les marchés.")); setItems(MOCK_MARKETS); }
    finally{ setLoading(false); }
  }, []);
  const loadAutoPriority=useCallback(async()=>{
    try{ const r=await adminGetMarketPriorityAuto(); setAutoPriority(!!r.data.enabled); }
    catch{ setAutoPriority(false); }
  }, []);
  useEffect(()=>{load();loadAutoPriority();},[load,loadAutoPriority]);
  const toggleAutoPriority=async()=>{
    setAutoPriorityBusy(true);
    try{
      const r=await adminSetMarketPriorityAuto(!autoPriority);
      setAutoPriority(!!r.data.enabled);
      toast.ok(r.data.enabled
        ? "Priorité automatique aléatoire activée — un tirage aura lieu toutes les 24h."
        : "Priorité automatique aléatoire désactivée — retour à la gestion manuelle.");
    }catch(err){ toast.err(extractErr(err,"Impossible de changer ce réglage.")); }
    finally{ setAutoPriorityBusy(false); }
  };
  const action=async(id,fn,msg)=>{
    setActing(id);
    try{ await fn(); toast.ok(msg); load(); }
    catch(err){ toast.err(extractErr(err,"Action échouée.")); }
    finally{ setActing(null); }
  };
  const displayed=items.filter(m=>filter==="ALL"||m.status===filter).filter(m=>!search||m.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>(b.display_priority||0)-(a.display_priority||0));
  const COLS="2fr 100px 100px 80px 90px 100px 170px";const MIN="700px";
  return (<>
    <Panel><PanelHead><PanelTitle>Markets ({items.length})</PanelTitle><PanelActions><Search placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/>{["ALL","DRAFT","OPEN","CLOSED","RESOLVED","CANCELLED"].map(f=><FilterChip key={f} $active={filter===f} onClick={()=>setFilter(f)}>{f}</FilterChip>)}<Btn $t={autoPriority?"ok":"warn"} disabled={autoPriority===null||autoPriorityBusy} onClick={toggleAutoPriority} title="Réattribue aléatoirement la priorité des marchés ouverts toutes les 24h. La méthode manuelle reste disponible quand c'est désactivé.">🎲 Priorité auto (24h) : {autoPriority===null?"…":autoPriority?"ON":"OFF"}</Btn><NewBtn onClick={()=>setModal({type:"create"})}>+ New Market</NewBtn></PanelActions></PanelHead>
      <TableScroll><THead $cols={COLS} $minW={MIN}><TH>Title</TH><TH>Category</TH><TH>Volume</TH><TH>Priority</TH><TH>Outcomes</TH><TH>Status</TH><TH>Actions</TH></THead>
      {loading?Array.from({length:4}).map((_,i)=><Skel key={i}/>):displayed.length===0?<Empty>No markets found.</Empty>:displayed.map(m=>(
        <TRow key={m.id} $cols={COLS} $minW={MIN}><Cell style={{whiteSpace:"normal",lineHeight:1.3,fontSize:".75rem"}}>{m.title}</Cell><Cell $dim>{m.category_name}</Cell><Cell $mono>{fmtVol(m.volume)}</Cell><Cell $mono>{m.display_priority||0}</Cell><Cell $dim>{m.outcomes?.length||0}</Cell><Cell><Pill $s={m.status}>{m.status}</Pill></Cell>
          <Cell><ActionBtns><Btn onClick={()=>setModal({type:"edit",market:m})}>✎ Edit</Btn>{m.status==="DRAFT"&&<Btn $t="ok" disabled={acting===m.id} onClick={()=>action(m.id,()=>adminPublishMarket(m.id),"Publié !")}>Publish</Btn>}{m.status==="OPEN"&&<Btn $t="warn" disabled={acting===m.id} onClick={()=>action(m.id,()=>adminCloseMarket(m.id),"Fermé.")}>Close</Btn>}{m.status==="CLOSED"&&<Btn $t="ok" onClick={()=>setModal({type:"resolve",market:m})}>Resolve</Btn>}{(m.status==="DRAFT"||m.status==="OPEN")&&<Btn $t="danger" disabled={acting===m.id} onClick={()=>action(m.id,()=>adminCancelMarket(m.id,{reason:"Cancelled by admin"}),"Annulé.")}>Cancel</Btn>}</ActionBtns></Cell>
        </TRow>))}</TableScroll>
    </Panel>
    {modal?.type==="create"&&<MarketFormModal categories={categories} onClose={()=>setModal(null)} onSaved={()=>{setModal(null);load();}} toast={toast}/>}
    {modal?.type==="edit"&&<MarketFormModal market={modal.market} categories={categories} onClose={()=>setModal(null)} onSaved={()=>{setModal(null);load();}} toast={toast}/>}
    {modal?.type==="resolve"&&<ResolveModal market={modal.market} onClose={()=>setModal(null)} onResolved={()=>{setModal(null);load();}} toast={toast}/>}
  </>);
}

function UsersTab({ toast }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);const[search,setSearch]=useState("");const[modal,setModal]=useState(null);
  const load=useCallback(async()=>{
    setLoading(true);
    try{ const r=await adminGetUsers(); setItems(r.data.results||r.data); }
    catch(err){ toast.err(extractErr(err,"Impossible de charger les utilisateurs.")); setItems(MOCK_USERS); }
    finally{ setLoading(false); }
  }, []);
  useEffect(()=>{load();},[load]);
  const toggleActive=async(u)=>{
    // Optimistic update
    setItems(prev=>prev.map(x=>x.id===u.id?{...x,is_active:!x.is_active}:x));
    try{
      await adminToggleActive(u.id);
      toast.ok(`${u.username} ${u.is_active?"désactivé":"activé"}.`);
    } catch(err){
      // Rollback
      setItems(prev=>prev.map(x=>x.id===u.id?{...x,is_active:u.is_active}:x));
      toast.err(extractErr(err,"Impossible de modifier ce compte."));
    }
  };
  const displayed=items.filter(u=>!search||u.username.toLowerCase().includes(search.toLowerCase())||u.email.toLowerCase().includes(search.toLowerCase()));
  const COLS="130px 1fr 110px 90px 90px 100px 130px";const MIN="700px";
  return (<Panel><PanelHead><PanelTitle>Users ({items.length})</PanelTitle><PanelActions><Search placeholder="Search users…" value={search} onChange={e=>setSearch(e.target.value)}/></PanelActions></PanelHead>
    <TableScroll><THead $cols={COLS} $minW={MIN}><TH>Username</TH><TH>Email</TH><TH>Role</TH><TH>Balance</TH><TH>Joined</TH><TH>Status</TH><TH>Actions</TH></THead>
    {loading?Array.from({length:4}).map((_,i)=><Skel key={i}/>):displayed.length===0?<Empty>No users found.</Empty>:displayed.map(u=>(
      <TRow key={u.id} $cols={COLS} $minW={MIN}><Cell $mono>{u.username}</Cell><Cell $dim style={{fontSize:".72rem"}}>{u.email}</Cell><Cell><Pill $s={u.role==="superadmin"||u.role==="staff"?"OPEN":u.role==="manager"?"RESOLVED":"DRAFT"}>{u.role||"viewer"}</Pill></Cell><Cell $mono style={{color:parseFloat(u.wallet?.usdc_balance||0)>0?"#00e676":"inherit"}}>${parseFloat(u.wallet?.usdc_balance||0).toFixed(2)}</Cell><Cell $dim>{fmt(u.date_joined)}</Cell><Cell><Pill $s={u.is_active?"active":"inactive"}>{u.is_active?"Active":"Inactive"}</Pill></Cell><Cell><ActionBtns><Btn onClick={()=>setModal({user:u})}>Set Role</Btn><Btn $t={u.is_active?"danger":"ok"} onClick={()=>toggleActive(u)}>{u.is_active?"Disable":"Enable"}</Btn></ActionBtns></Cell>
      </TRow>))}</TableScroll>
    {modal&&<UserRoleModal user={modal.user} onClose={()=>setModal(null)} onSaved={()=>{setModal(null);load();}} toast={toast}/>}
  </Panel>);
}

function WithdrawalsTab({ toast }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);const[filter,setFilter]=useState("PENDING");const[modal,setModal]=useState(null);const[acting,setActing]=useState(null);
  const load=useCallback(async()=>{
    setLoading(true);
    try{ const r=await adminGetWithdrawals({status:filter!=="ALL"?filter:undefined}); setItems(r.data.results||r.data); }
    catch(err){ toast.err(extractErr(err,"Impossible de charger les retraits.")); setItems(MOCK_WITHDRAWALS.filter(w=>filter==="ALL"||w.status===filter)); }
    finally{ setLoading(false); }
  }, [filter]);
  useEffect(()=>{load();},[load]);
  const approve=async(id)=>{
    setActing(id);
    try{
      await adminApproveWithdrawal(id);
      setItems(prev=>prev.map(w=>w.id===id?{...w,status:"APPROVED"}:w));
      toast.ok("Retrait approuvé.");
    } catch(err){
      toast.err(extractErr(err,"Impossible d'approuver ce retrait."));
    } finally{
      setActing(null);
    }
  };
  const COLS="100px 1fr 80px 90px 100px 80px 160px";const MIN="680px";
  return (<Panel><PanelHead><PanelTitle>Withdrawal Requests</PanelTitle><PanelActions>{["ALL","PENDING","APPROVED","REJECTED","COMPLETED"].map(f=><FilterChip key={f} $active={filter===f} onClick={()=>setFilter(f)}>{f}</FilterChip>)}</PanelActions></PanelHead>
    <TableScroll><THead $cols={COLS} $minW={MIN}><TH>User</TH><TH>Address</TH><TH>Coin</TH><TH>Amount</TH><TH>Date</TH><TH>Status</TH><TH>Actions</TH></THead>
    {loading?Array.from({length:3}).map((_,i)=><Skel key={i}/>):items.length===0?<Empty>No {filter.toLowerCase()} requests.</Empty>:items.map(w=>(
      <TRow key={w.id} $cols={COLS} $minW={MIN}><Cell $mono>{w.user||"—"}</Cell><Cell $dim style={{fontSize:".7rem"}}>{truncAddr(w.address)}</Cell><Cell $mono>{w.coin}</Cell><Cell $mono>${parseFloat(w.amount).toFixed(2)}</Cell><Cell $dim>{fmt(w.created_at)}</Cell><Cell><Pill $s={w.status}>{w.status}</Pill></Cell><Cell>{w.status==="PENDING"?<ActionBtns><Btn $t="ok" disabled={acting===w.id} onClick={()=>approve(w.id)}>{acting===w.id?"…":"✓ Approve"}</Btn><Btn $t="danger" onClick={()=>setModal({wd:w})}>✕ Reject</Btn></ActionBtns>:<Cell $dim>—</Cell>}</Cell>
      </TRow>))}</TableScroll>
    {modal&&<RejectModal wd={modal.wd} onClose={()=>setModal(null)} onRejected={()=>{setModal(null);load();}} toast={toast}/>}
  </Panel>);
}

function CategoryEditModal({ category, onClose, onSaved, toast }) {
  const [names, setNames] = useState({
    fr: category.name_fr || category.name || "",
    en: category.name_en || "",
    es: category.name_es || "",
    de: category.name_de || "",
    pt: category.name_pt || "",
    ar: category.name_ar || "",
  });
  const [loading,     setLoading]     = useState(false);
  const [translating, setTranslating] = useState(false);

  const handleAutoTranslate = async () => {
    if (!names.fr.trim()) {
      toast.err("Saisis d'abord le nom en français.");
      return;
    }
    setTranslating(true);
    try {
      const r = await adminAutoTranslate(names.fr.trim(), "fr");
      setNames(n => {
        const next = { ...n };
        ["en","es","de","pt","ar"].forEach(l => {
          if (r.data[l] && !n[l]?.trim()) next[l] = r.data[l];
        });
        return next;
      });
      toast.ok("Traductions générées — vérifie et sauvegarde.");
    } catch (err) {
      toast.err(extractErr(err, "Traduction échouée."));
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      // Utilise adminUpdateCategory — à créer dans adminClient.js
      const payload = {
        name:    names.fr.trim(),   // synchronise la source
        name_fr: names.fr.trim(),
        name_en: names.en.trim(),
        name_es: names.es.trim(),
        name_de: names.de.trim(),
        name_pt: names.pt.trim(),
        name_ar: names.ar.trim(),
      };
      const res = await adminUpdateCategory(category.id, payload);
      onSaved(res.data);
    } catch (err) {
      toast.err(extractErr(err, "Sauvegarde échouée."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox $w="520px">
        <MHead>
          <MTitle>Edit Category — {category.name_fr || category.name}</MTitle>
          <MClose onClick={onClose}>✕</MClose>
        </MHead>
        <MBody>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <TranslateBtn
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating || !names.fr.trim()}
            >
              {translating ? "Traduction…" : "🪄 Auto-traduire depuis FR"}
            </TranslateBtn>
          </div>

          {[
            {code:"fr", label:"🇫🇷 Français (source)"},
            {code:"en", label:"🇬🇧 English"},
            {code:"es", label:"🇪🇸 Español"},
            {code:"de", label:"🇩🇪 Deutsch"},
            {code:"pt", label:"🇵🇹 Português"},
            {code:"ar", label:"🇸🇦 العربية"},
          ].map(({code, label}) => (
            <Field key={code}>
              <Label>{label}</Label>
              <Input
                dir={code === "ar" ? "rtl" : "ltr"}
                value={names[code]}
                onChange={e => setNames(n => ({...n, [code]: e.target.value}))}
                placeholder={code === "fr" ? "Crypto, Sports, Politique…" : "—"}
              />
            </Field>
          ))}
        </MBody>
        <MFoot>
          <CancelBtn2 onClick={onClose}>Cancel</CancelBtn2>
          <SaveBtn disabled={loading || !names.fr.trim()} onClick={save}>
            {loading ? "Saving…" : "Save Changes"}
          </SaveBtn>
        </MFoot>
      </ModalBox>
    </Overlay>
  );
}

function CategoriesTab({ categories, setCategories, toast }) {
  const [newCat, setNewCat] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // catégorie en cours d'édition

  const add = async () => {
    if (!newCat.trim()) return;
    setLoading(true);
    try {
      // Création : on envoie juste le nom FR, le backend gère le reste
      const res = await adminCreateCategory({
        name: newCat.trim(),
        name_fr: newCat.trim(),
      });
      setCategories(p => [...p, res.data]);
      toast.ok(`Catégorie "${newCat.trim()}" ajoutée. Pense à la traduire.`);
      setNewCat("");
    } catch (err) {
      toast.err(extractErr(err, "Impossible de créer cette catégorie."));
    } finally {
      setLoading(false);
    }
  };

  const del = async (cat) => {
    if (!window.confirm(`Supprimer "${cat.name}" ?`)) return;
    try {
      await adminDeleteCategory(cat.id);
      setCategories(p => p.filter(x => x.id !== cat.id));
      toast.ok(`"${cat.name}" supprimée.`);
    } catch (err) {
      toast.err(extractErr(err, "Suppression impossible."));
    }
  };

  return (
    <>
      <Panel>
        <PanelHead>
          <PanelTitle>Categories ({categories.length})</PanelTitle>
          <PanelActions>
            <Input
              placeholder="Nouveau nom en français…"
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              onKeyDown={e => e.key === "Enter" && add()}
              style={{width:"220px"}}
            />
            <SaveBtn disabled={!newCat.trim() || loading} onClick={add} style={{padding:".42rem .9rem"}}>
              {loading ? "Adding…" : "+ Add"}
            </SaveBtn>
          </PanelActions>
        </PanelHead>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",
          gap:".75rem",padding:"1.25rem"}}>
          {categories.map(c => {
            const missingLangs = ["en","es","de","pt","ar"].filter(l => !c[`name_${l}`]);
            return (
              <div key={c.id} style={{display:"flex",flexDirection:"column",gap:".4rem",
                background:"#0e1115",border:"1px solid #1e2530",borderRadius:"8px",padding:".7rem .9rem"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:".82rem",fontWeight:500}}>{c.name_fr || c.name}</span>
                  <div style={{display:"flex",gap:".3rem"}}>
                    <button onClick={() => setEditing(c)} style={{background:"none",border:"none",
                      color:"#4fc3f7",cursor:"pointer",fontSize:".7rem"}}>✎</button>
                    <button onClick={() => del(c)} style={{background:"none",border:"none",
                      color:"#5a6a7a",cursor:"pointer",fontSize:".85rem"}}>✕</button>
                  </div>
                </div>
                <div style={{display:"flex",gap:".25rem",flexWrap:"wrap"}}>
                  {["fr","en","es","de","pt","ar"].map(l => (
                    <span key={l} style={{
                      fontFamily:"DM Mono,monospace",fontSize:".55rem",
                      padding:".1rem .35rem",borderRadius:"3px",
                      background: c[`name_${l}`] ? "rgba(0,230,118,.12)" : "rgba(255,193,7,.08)",
                      color: c[`name_${l}`] ? "#00e676" : "#ffc107",
                    }}>
                      {l.toUpperCase()}{c[`name_${l}`] ? " ✓" : " —"}
                    </span>
                  ))}
                </div>
                {missingLangs.length > 0 && (
                  <span style={{fontSize:".62rem",color:"#5a6a7a"}}>
                    Manque : {missingLangs.join(", ").toUpperCase()}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {editing && (
        <CategoryEditModal
          category={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setCategories(p => p.map(c => c.id === updated.id ? updated : c));
            setEditing(null);
            toast.ok("Catégorie mise à jour.");
          }}
          toast={toast}
        />
      )}
    </>
  );
}
/* ─── Root ────────────────────────────────────────────────────────────────── */
const TABS = [
  { id:"overview",     label:"Overview",    icon:"◉" },
  { id:"markets",      label:"Markets",     icon:"📊" },
  { id:"users",        label:"Users",       icon:"👥" },
  { id:"countries",    label:"Countries",   icon:"🌍" },
  { id:"withdrawals",  label:"Crypto Wd.",  icon:"💸" },
  { id:"payouts",      label:"Payouts",     icon:"💳" },
  { id:"referrals",    label:"Referrals",   icon:"🤝" },
  { id:"emails",       label:"Emails",      icon:"📧" },   // ← NOUVEAU
  { id:"categories",   label:"Categories",  icon:"🏷" },
];

function ReferralsTab({ toast }) {
  const [stats,   setStats]   = useState(null);
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [modal,   setModal]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        adminGetReferralStats(),
        adminGetReferralList({ search: search || undefined }),
      ]);
      setStats(s.data);
      setList(l.data.results || l.data);
    } catch (err) {
      toast.err(extractErr(err, "Impossible de charger le parrainage."));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const statsCards = stats ? [
    { label:"Parrains actifs",      val: stats.unique_referrers,                        c:"accent" },
    { label:"Filleuls qualifiés",   val: `${stats.qualifying_count}/${stats.total_relations}`, c:"text" },
    { label:"Relations flaguées",   val: stats.flagged_count,                           c: stats.flagged_count > 0 ? "red" : "text" },
    { label:"EVP en hold (PENDING)", val: parseFloat(stats.evp_pending).toFixed(0),     c:"gold" },
    { label:"EVP versés (APPROVED)", val: parseFloat(stats.evp_approved).toFixed(0),    c:"green" },
    { label:"Prêtes à payer",       val: stats.ready_to_pay,                            c: stats.ready_to_pay > 0 ? "gold" : "text" },
  ] : [];

  const COLS = "120px 1fr 90px 80px 110px 130px 90px";
  const MIN  = "880px";

  return (
    <>
      {/* Stats */}
      <StatGrid>
        {loading && !stats
          ? Array.from({length:6}).map((_,i) => <Skel key={i} style={{height:"68px"}}/>)
          : statsCards.map((x, i) => (
              <StatCard key={x.label} $i={i}>
                <StatVal $c={x.c}>{x.val}</StatVal>
                <StatLbl>{x.label}</StatLbl>
              </StatCard>
            ))
        }
      </StatGrid>

      {/* Alerte commissions prêtes */}
      {stats?.ready_to_pay > 0 && (
        <Panel>
          <PanelHead>
            <PanelTitle>
              ⏰ {stats.ready_to_pay} commission(s) PENDING ont atteint J+30 et peuvent être approuvées
            </PanelTitle>
          </PanelHead>
        </Panel>
      )}

      {/* Tops */}
      {stats && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem",marginBottom:"1.5rem"}}>
          <Panel>
            <PanelHead><PanelTitle>🏆 Top par filleuls</PanelTitle></PanelHead>
            <div style={{padding:".5rem 0"}}>
              {stats.top_by_referees.length === 0
                ? <Empty>Aucune donnée.</Empty>
                : stats.top_by_referees.map((t, i) => (
                    <div key={t.user_id} style={{display:"flex",alignItems:"center",gap:".75rem",
                      padding:".55rem 1.25rem",
                      borderBottom: i < stats.top_by_referees.length-1 ? "1px solid #1e2530" : "none"}}>
                      <span style={{fontFamily:"DM Mono,monospace",fontSize:".75rem",fontWeight:700,
                        color: i === 0 ? "#ffc107" : i === 1 ? "#e8edf2" : "#5a6a7a", width:"24px"}}>
                        #{i + 1}
                      </span>
                      <span style={{flex:1,fontSize:".8rem",fontWeight:500}}>{t.username}</span>
                      <span style={{fontFamily:"DM Mono,monospace",fontSize:".75rem",fontWeight:700,color:"#4fc3f7"}}>
                        {t.count} filleul{t.count > 1 ? "s" : ""}
                      </span>
                    </div>
                  ))
              }
            </div>
          </Panel>

          <Panel>
            <PanelHead><PanelTitle>💰 Top par EVP versés</PanelTitle></PanelHead>
            <div style={{padding:".5rem 0"}}>
              {stats.top_by_commission.length === 0
                ? <Empty>Aucune commission versée.</Empty>
                : stats.top_by_commission.map((t, i) => (
                    <div key={t.user_id} style={{display:"flex",alignItems:"center",gap:".75rem",
                      padding:".55rem 1.25rem",
                      borderBottom: i < stats.top_by_commission.length-1 ? "1px solid #1e2530" : "none"}}>
                      <span style={{fontFamily:"DM Mono,monospace",fontSize:".75rem",fontWeight:700,
                        color: i === 0 ? "#ffc107" : i === 1 ? "#e8edf2" : "#5a6a7a", width:"24px"}}>
                        #{i + 1}
                      </span>
                      <span style={{flex:1,fontSize:".8rem",fontWeight:500}}>{t.username}</span>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:".1rem"}}>
                        <span style={{fontFamily:"DM Mono,monospace",fontSize:".75rem",fontWeight:700,color:"#00e676"}}>
                          {parseFloat(t.total_evp).toFixed(0)} EVP
                        </span>
                        <span style={{fontFamily:"DM Mono,monospace",fontSize:".6rem",color:"#5a6a7a"}}>
                          ${parseFloat(t.total_usd).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))
              }
            </div>
          </Panel>
        </div>
      )}

      {/* Liste */}
      <Panel>
        <PanelHead>
          <PanelTitle>Tous les parrains ({list.length})</PanelTitle>
          <PanelActions>
            <Search placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)}/>
          </PanelActions>
        </PanelHead>

        <TableScroll>
          <THead $cols={COLS} $minW={MIN}>
            <TH>Parrain</TH><TH>Email</TH><TH>Code</TH><TH>Taux</TH>
            <TH>Filleuls</TH><TH>EVP (pending / paid)</TH><TH>Action</TH>
          </THead>

          {loading
            ? Array.from({length:4}).map((_,i) => <Skel key={i}/>)
            : list.length === 0
            ? <Empty>Aucun parrain.</Empty>
            : list.map(r => (
                <TRow key={r.user_id} $cols={COLS} $minW={MIN}>
                  <Cell $mono>{r.username}</Cell>
                  <Cell $dim style={{fontSize:".72rem"}}>{r.email}</Cell>
                  <Cell $mono style={{color: r.code_active ? "#4fc3f7" : "#5a6a7a"}}>
                    {r.code || "—"}{!r.code_active && r.code && " (off)"}
                  </Cell>
                  <Cell $mono>{(parseFloat(r.commission_rate) * 100).toFixed(1)}%</Cell>
                  <Cell $mono>
                    {r.referees_count}
                    {r.flagged_count > 0 && (
                      <span style={{color:"#ff3b5c",marginLeft:".3rem",fontSize:".68rem"}}>
                        ({r.flagged_count} ⚠)
                      </span>
                    )}
                  </Cell>
                  <Cell>
                    <span style={{fontFamily:"DM Mono,monospace",fontSize:".72rem"}}>
                      <span style={{color:"#ffc107"}}>{parseFloat(r.evp_pending).toFixed(0)}</span>
                      {" / "}
                      <span style={{color:"#00e676"}}>{parseFloat(r.evp_approved).toFixed(0)}</span>
                    </span>
                  </Cell>
                  <Cell>
                    <Btn onClick={() => setModal({userId:r.user_id, username:r.username})}>
                      📋 Détails
                    </Btn>
                  </Cell>
                </TRow>
              ))
          }
        </TableScroll>
      </Panel>

      {modal && (
        <ReferralDetailModal
          userId={modal.userId}
          username={modal.username}
          onClose={() => setModal(null)}
          onUpdated={load}
          toast={toast}
        />
      )}
    </>
  );
}


function ReferralDetailModal({ userId, username, onClose, onUpdated, toast }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting,  setActing]  = useState(null);
  const [view,    setView]    = useState("referees");   // referees | commissions

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminGetReferralDetail(userId);
      setData(r.data);
    } catch (err) {
      toast.err(extractErr(err, "Impossible de charger le détail."));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    if (!window.confirm("Approuver et créditer maintenant cette commission ?")) return;
    setActing(id);
    try {
      const r = await adminApproveCommission(id);
      toast.ok(`+${r.data.evp_credited} EVP crédités à ${r.data.referrer}.`);
      await load();
      onUpdated?.();
    } catch (err) {
      toast.err(extractErr(err, "Impossible d'approuver."));
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Motif du rejet (obligatoire) :");
    if (!reason?.trim()) return;
    setActing(id);
    try {
      await adminRejectCommission(id, { reason });
      toast.ok("Commission rejetée.");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.err(extractErr(err, "Impossible de rejeter."));
    } finally {
      setActing(null);
    }
  };

  const handleFlag = async (relationId, currentlyFlagged) => {
    const reason = currentlyFlagged ? "" : window.prompt("Motif du flag (fraude suspectée) :");
    if (!currentlyFlagged && !reason?.trim()) return;
    try {
      await adminFlagRelation(relationId, { flagged: !currentlyFlagged, reason: reason || "" });
      toast.ok(currentlyFlagged ? "Flag retiré." : "Relation flaguée.");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.err(extractErr(err, "Action impossible."));
    }
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox $w="820px">
        <MHead>
          <MTitle>🤝 <span style={{color:"#ffc107"}}>{username}</span></MTitle>
          <MClose onClick={onClose}>✕</MClose>
        </MHead>

        <MBody>
          {loading
            ? <div style={{textAlign:"center",padding:"2rem",color:"#5a6a7a"}}>Chargement…</div>
            : !data
            ? <Empty>Aucune donnée.</Empty>
            : <>
                {/* Code + config */}
                {data.code && (
                  <div style={{display:"flex",gap:"1rem",flexWrap:"wrap",padding:".75rem .9rem",
                    background:"#080a0c",border:"1px solid #1e2530",borderRadius:"7px",
                    fontSize:".75rem"}}>
                    <span><span style={{color:"#5a6a7a"}}>Code :</span>{" "}
                      <strong style={{color: data.code.is_active ? "#00e676" : "#5a6a7a",
                        fontFamily:"DM Mono,monospace"}}>
                        {data.code.code}
                      </strong>
                    </span>
                    <span><span style={{color:"#5a6a7a"}}>Taux :</span>{" "}
                      <strong style={{color:"#4fc3f7"}}>{(parseFloat(data.code.commission_rate)*100).toFixed(1)}%</strong>
                    </span>
                    <span><span style={{color:"#5a6a7a"}}>Cap mensuel :</span>{" "}
                      <strong style={{color:"#ffc107"}}>${data.code.monthly_cap_usd}</strong>
                    </span>
                  </div>
                )}

                {/* Toggle */}
                <div style={{display:"flex",gap:".4rem"}}>
                  <FilterChip $active={view==="referees"} onClick={() => setView("referees")}>
                    Filleuls ({data.totals.referees_count})
                  </FilterChip>
                  <FilterChip $active={view==="commissions"} onClick={() => setView("commissions")}>
                    Commissions ({data.commissions.length})
                  </FilterChip>
                </div>

                {/* Filleuls */}
                {view === "referees" && (
                  data.referees.length === 0
                  ? <Empty>Aucun filleul.</Empty>
                  : <div style={{border:"1px solid #1e2530",borderRadius:"8px",overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:".7rem",minWidth:"720px"}}>
                        <thead>
                          <tr style={{background:"#080a0c"}}>
                            {["Filleul","Inscrit","Statut","Qualification","Volume","EVP (P/A)","Action"].map(h => (
                              <th key={h} style={{padding:".5rem .65rem",textAlign:"left",
                                fontFamily:"DM Mono,monospace",fontSize:".6rem",color:"#5a6a7a",
                                textTransform:"uppercase",letterSpacing:".06em",
                                borderBottom:"1px solid #1e2530"}}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.referees.map(f => (
                            <tr key={f.relation_id} style={{
                              borderBottom:"1px solid #1e2530",
                              background: f.flagged ? "rgba(255,59,92,.04)" : "transparent",
                            }}>
                              <td style={{padding:".55rem .65rem"}}>
                                <div style={{fontFamily:"DM Mono,monospace",fontSize:".72rem",color:"#e8edf2"}}>
                                  {f.username} {f.flagged && "🚩"}
                                </div>
                                <div style={{fontSize:".62rem",color:"#5a6a7a"}}>{f.email}</div>
                                {f.ip_at_signup && (
                                  <div style={{fontSize:".58rem",color:"#5a6a7a",fontFamily:"DM Mono,monospace"}}>
                                    IP: {f.ip_at_signup}
                                  </div>
                                )}
                              </td>
                              <td style={{padding:".55rem .65rem",fontSize:".68rem",color:"#8a9aaa"}}>
                                {fmt(f.referred_at)}
                              </td>
                              <td style={{padding:".55rem .65rem"}}>
                                <Pill $s={f.is_active ? "active" : "inactive"}>
                                  {f.is_active ? "Active" : "Off"}
                                </Pill>
                              </td>
                              <td style={{padding:".55rem .65rem",fontSize:".65rem",fontFamily:"DM Mono,monospace"}}>
                                <div style={{color: f.qualification.phone_verified ? "#00e676" : "#5a6a7a"}}>
                                  📞 {f.qualification.phone_verified ? "OK" : "—"}
                                </div>
                                <div style={{color: f.qualification.trades_count >= f.qualification.trades_required ? "#00e676" : "#5a6a7a"}}>
                                  🎯 {f.qualification.trades_count}/{f.qualification.trades_required}
                                </div>
                                <div style={{color: f.qualification.active_days >= f.qualification.days_required ? "#00e676" : "#5a6a7a"}}>
                                  📅 {f.qualification.active_days}/{f.qualification.days_required}d
                                </div>
                              </td>
                              <td style={{padding:".55rem .65rem",fontFamily:"DM Mono,monospace",fontSize:".7rem"}}>
                                ${parseFloat(f.withdrawal_volume).toFixed(0)}
                              </td>
                              <td style={{padding:".55rem .65rem",fontFamily:"DM Mono,monospace",fontSize:".68rem"}}>
                                <span style={{color:"#ffc107"}}>{parseFloat(f.commission_pending).toFixed(0)}</span>
                                {" / "}
                                <span style={{color:"#00e676"}}>{parseFloat(f.commission_approved).toFixed(0)}</span>
                              </td>
                              <td style={{padding:".55rem .65rem"}}>
                                <Btn
                                  $t={f.flagged ? "ok" : "danger"}
                                  onClick={() => handleFlag(f.relation_id, f.flagged)}
                                  style={{fontSize:".58rem",padding:".18rem .45rem"}}
                                >
                                  {f.flagged ? "Unflag" : "Flag"}
                                </Btn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                )}

                {/* Commissions */}
                {view === "commissions" && (
                  data.commissions.length === 0
                  ? <Empty>Aucune commission.</Empty>
                  : <div style={{border:"1px solid #1e2530",borderRadius:"8px",overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:".7rem",minWidth:"720px"}}>
                        <thead>
                          <tr style={{background:"#080a0c"}}>
                            {["Filleul","Retrait","Commission","Status","Disponible","Actions"].map(h => (
                              <th key={h} style={{padding:".5rem .65rem",textAlign:"left",
                                fontFamily:"DM Mono,monospace",fontSize:".6rem",color:"#5a6a7a",
                                textTransform:"uppercase",letterSpacing:".06em",
                                borderBottom:"1px solid #1e2530"}}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.commissions.map(c => {
                            const ripe = new Date(c.available_at) <= new Date();
                            return (
                              <tr key={c.id} style={{borderBottom:"1px solid #1e2530"}}>
                                <td style={{padding:".55rem .65rem",fontFamily:"DM Mono,monospace",fontSize:".7rem"}}>
                                  {c.referee}
                                </td>
                                <td style={{padding:".55rem .65rem",fontFamily:"DM Mono,monospace",fontSize:".7rem"}}>
                                  ${parseFloat(c.withdrawal_amount_usd).toFixed(2)}
                                </td>
                                <td style={{padding:".55rem .65rem",fontFamily:"DM Mono,monospace",fontSize:".7rem"}}>
                                  <div style={{color:"#4fc3f7"}}>{parseFloat(c.commission_amount_evp).toFixed(0)} EVP</div>
                                  <div style={{fontSize:".58rem",color:"#5a6a7a"}}>
                                    ${parseFloat(c.commission_amount_usd).toFixed(2)}
                                  </div>
                                </td>
                                <td style={{padding:".55rem .65rem"}}>
                                  <Pill $s={c.status==="APPROVED"?"OPEN":c.status==="PENDING"?"PENDING":"REJECTED"}>
                                    {c.status}
                                  </Pill>
                                </td>
                                <td style={{padding:".55rem .65rem",fontSize:".65rem",
                                  color: ripe && c.status==="PENDING" ? "#ffc107" : "#8a9aaa"}}>
                                  {fmt(c.available_at)}{ripe && c.status==="PENDING" && " ⏰"}
                                </td>
                                <td style={{padding:".55rem .65rem"}}>
                                  {c.status === "PENDING" && (
                                    <ActionBtns>
                                      <Btn $t="ok" disabled={acting===c.id}
                                        onClick={() => handleApprove(c.id)}
                                        style={{fontSize:".58rem",padding:".18rem .45rem"}}>
                                        ✓
                                      </Btn>
                                      <Btn $t="danger" disabled={acting===c.id}
                                        onClick={() => handleReject(c.id)}
                                        style={{fontSize:".58rem",padding:".18rem .45rem"}}>
                                        ✕
                                      </Btn>
                                    </ActionBtns>
                                  )}
                                  {c.paid_at && (
                                    <span style={{fontSize:".6rem",color:"#5a6a7a"}}>
                                      Payé {fmt(c.paid_at)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                )}
              </>
          }
        </MBody>

        <MFoot>
          <CancelBtn2 onClick={onClose}>Fermer</CancelBtn2>
        </MFoot>
      </ModalBox>
    </Overlay>
  );
}

function PayoutsTab({ toast }) {
  const [items,  setItems]  = useState([]);
  const [loading,setLoading]= useState(true);
  const [statusF,setStatusF]= useState("PENDING");
  const [methodF,setMethodF]= useState("ALL");
  const [acting, setActing] = useState(null);
  const [modal,  setModal]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF !== "ALL") params.status = statusF;
      if (methodF !== "ALL") params.method = methodF;
      const r = await adminGetPayouts(params);
      setItems(r.data.results || r.data);
    } catch (err) {
      toast.err(extractErr(err, "Impossible de charger les payouts."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusF, methodF]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action, p, body = {}) => {
    setActing(p.id);
    try {
      if (action === "approve")  await adminApprovePayout(p.id, body);
      if (action === "complete") await adminCompletePayout(p.id, body);
      if (action === "reject")   await adminRejectPayout(p.id, body);
      toast.ok(
        action === "approve"  ? "Demande approuvée." :
        action === "complete" ? "Paiement marqué comme effectué." :
                                "Demande rejetée — fonds remboursés."
      );
      load();
    } catch (err) {
      toast.err(extractErr(err, "Action échouée."));
    } finally {
      setActing(null);
      setModal(null);
    }
  };

  const counts = items.reduce((acc, p) => {
    acc[p.method] = (acc[p.method] || 0) + 1;
    return acc;
  }, {});

  const COLS = "90px 130px 1fr 80px 90px 100px 110px 220px";
  const MIN  = "980px";

  return (
    <>
      <Panel>
        <PanelHead>
          <PanelTitle>Payouts ({items.length})</PanelTitle>
          <PanelActions>
            {["ALL","PENDING","APPROVED","COMPLETED","REJECTED","CANCELLED"].map(f => (
              <FilterChip key={f} $active={statusF===f} onClick={()=>setStatusF(f)}>{f}</FilterChip>
            ))}
          </PanelActions>
        </PanelHead>

        {/* Sub-filter : par méthode */}
        <div style={{display:"flex",gap:".4rem",padding:".7rem 1.25rem",
          background:"#0a0d11",borderBottom:"1px solid #1e2530",
          alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:".62rem",color:"#5a6a7a",textTransform:"uppercase",
            letterSpacing:".08em",fontFamily:"DM Mono,monospace",marginRight:".4rem"}}>
            Method:
          </span>
          <FilterChip $active={methodF==="ALL"} onClick={()=>setMethodF("ALL")}>
            All <span style={{marginLeft:".4rem",opacity:.6}}>({items.length})</span>
          </FilterChip>
          {["mobile","bank","paypal"].map(m => {
            const meta = METHOD_META[m];
            return (
              <FilterChip key={m} $active={methodF===m} onClick={()=>setMethodF(m)}>
                <span style={{marginRight:".3rem"}}>{meta.icon}</span>
                {meta.label}
                <span style={{marginLeft:".4rem",opacity:.6}}>({counts[m] || 0})</span>
              </FilterChip>
            );
          })}
        </div>

        <TableScroll>
          <THead $cols={COLS} $minW={MIN}>
            <TH>User</TH><TH>Method</TH><TH>Destination</TH>
            <TH>USD</TH><TH>EVP</TH><TH>Date</TH>
            <TH>Status</TH><TH>Actions</TH>
          </THead>

          {loading
            ? Array.from({length:3}).map((_,i) => <Skel key={i}/>)
            : items.length === 0
            ? <Empty>No {statusF.toLowerCase()} requests{methodF!=="ALL"?` for ${METHOD_META[methodF].label}`:""}.</Empty>
            : items.map(p => {
              const meta = METHOD_META[p.method] || { label: p.method, icon: "•", color: "#888" };
              return (
                <TRow key={p.id} $cols={COLS} $minW={MIN}>
                  <Cell $mono>{p.user || "—"}</Cell>
                  <Cell>
                    <span style={{display:"inline-flex",alignItems:"center",gap:".35rem",
                      fontFamily:"DM Mono,monospace",fontSize:".68rem",
                      padding:".15rem .55rem",borderRadius:"100px",
                      background:`${meta.color}1a`,color:meta.color,
                      border:`1px solid ${meta.color}44`,whiteSpace:"nowrap"}}>
                      <span>{meta.icon}</span>
                      {meta.label}
                    </span>
                  </Cell>
                  <Cell $dim style={{fontSize:".72rem",fontFamily:"DM Mono,monospace"}}>
                    {p.destination}
                  </Cell>
                  <Cell $mono>${parseFloat(p.amount_usd).toFixed(2)}</Cell>
                  <Cell $mono style={{color:"#5a6a7a"}}>{parseFloat(p.amount_evp).toFixed(0)}</Cell>
                  <Cell $dim>{fmt(p.created_at)}</Cell>
                  <Cell><Pill $s={p.status}>{p.status}</Pill></Cell>
                  <Cell>
                    {p.status === "PENDING" && (
                      <ActionBtns>
                        <Btn $t="ok"     disabled={acting===p.id} onClick={() => setModal({type:"complete", p})}>✓ Payé</Btn>
                        <Btn $t="warn"   disabled={acting===p.id} onClick={() => setModal({type:"approve",  p})}>⏳ En cours</Btn>
                        <Btn $t="danger" disabled={acting===p.id} onClick={() => setModal({type:"reject",   p})}>✕ Rejeter</Btn>
                      </ActionBtns>
                    )}
                    {p.status === "APPROVED" && (
                      <ActionBtns>
                        <Btn $t="ok"     disabled={acting===p.id} onClick={() => setModal({type:"complete", p})}>✓ Marquer payé</Btn>
                        <Btn $t="danger" disabled={acting===p.id} onClick={() => setModal({type:"reject",   p})}>✕ Rejeter</Btn>
                      </ActionBtns>
                    )}
                    {(p.status === "COMPLETED" || p.status === "REJECTED" || p.status === "CANCELLED") && (
                      <Cell $dim style={{fontSize:".68rem",fontFamily:"DM Mono,monospace",whiteSpace:"normal"}}>
                        {p.payout_reference && <>Ref: {p.payout_reference}</>}
                        {p.reject_reason   && <>{p.reject_reason}</>}
                        {!p.payout_reference && !p.reject_reason && "—"}
                      </Cell>
                    )}
                  </Cell>
                </TRow>
              );
            })
          }
        </TableScroll>
      </Panel>

      {modal && (
        <PayoutActionModal
          type={modal.type}
          p={modal.p}
          acting={acting === modal.p.id}
          onClose={() => setModal(null)}
          onConfirm={(body) => handleAction(modal.type, modal.p, body)}
        />
      )}
    </>
  );
}


// ── 5) AJOUTER le modal d'action ─────────────────────────────────────────────

function PayoutActionModal({ type, p, acting, onClose, onConfirm }) {
  const [ref,    setRef]    = useState("");
  const [reason, setReason] = useState("");

  const isReject = type === "reject";
  const meta     = METHOD_META[p.method] || { label: p.method, icon: "•" };

  const title = type === "approve"  ? "Approuver — paiement en cours"
              : type === "complete" ? "Marquer comme payé"
              :                       "Rejeter la demande";

  const refPlaceholder =
    p.method === "mobile" ? "ex: MP240315.1530.B91012"          :
    p.method === "bank"   ? "ex: SEPA-2026-03-15-001"           :
    p.method === "paypal" ? "ex: PAYID-MZX7XYZ123456789ABCD"    :
                            "Référence de la transaction";

  const refHelp =
    p.method === "mobile" ? "ID de la transaction côté MoMo / Orange Money / Wave" :
    p.method === "bank"   ? "Référence du virement bancaire émis"                   :
    p.method === "paypal" ? "Transaction ID PayPal (visible dans l'historique)"     :
                            "ID externe de la transaction";

  const handle = () => {
    if (isReject && !reason.trim()) return;
    onConfirm(isReject ? { reason } : { payout_reference: ref });
  };

  // Détails de destination selon méthode
  const renderDestinationDetails = () => {
    const d = p.payout_details || {};
    if (p.method === "mobile") {
      return <><strong style={{color:"#e8edf2"}}>+{d.phone}</strong> ({d.country_code})</>;
    }
    if (p.method === "bank") {
      return (
        <>
          <strong style={{color:"#e8edf2"}}>{d.account_name}</strong>
          <br/>
          <span style={{fontFamily:"DM Mono,monospace",fontSize:".7rem",lineHeight:1.5}}>
            IBAN: {d.iban}<br/>
            BIC: {d.bic}
          </span>
        </>
      );
    }
    if (p.method === "paypal") {
      return <strong style={{color:"#e8edf2"}}>{d.email}</strong>;
    }
    return p.destination;
  };

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox $w="460px">
        <MHead><MTitle>{title}</MTitle><MClose onClick={onClose}>✕</MClose></MHead>
        <MBody>
          {/* Récap */}
          <div style={{fontSize:".78rem",color:"#8a9aaa",lineHeight:1.6,
            padding:".75rem .9rem",background:"#080a0c",
            border:"1px solid #1e2530",borderRadius:"7px"}}>
            <div style={{marginBottom:".4rem"}}>
              <strong style={{color:"#e8edf2"}}>{p.user}</strong> demande{" "}
              <strong style={{color:"#e8edf2"}}>${parseFloat(p.amount_usd).toFixed(2)} USD</strong>{" "}
              via {meta.icon} <strong style={{color:"#e8edf2"}}>{meta.label}</strong>
            </div>
            <div style={{paddingTop:".5rem",borderTop:"1px solid #1e2530",fontSize:".74rem"}}>
              {renderDestinationDetails()}
            </div>
            <div style={{marginTop:".5rem",fontSize:".68rem",color:"#5a6a7a",
              fontFamily:"DM Mono,monospace"}}>
              Débit wallet : {parseFloat(p.amount_evp).toFixed(0)} EVP · Currency : {p.currency}
            </div>
          </div>

          {!isReject && (
            <Field>
              <Label>Référence du paiement {type === "complete" && <Required>*</Required>}</Label>
              <Input placeholder={refPlaceholder} value={ref} onChange={e => setRef(e.target.value)}/>
              <span style={{fontSize:".65rem",color:"#5a6a7a"}}>{refHelp}</span>
            </Field>
          )}

          {isReject && (
            <Field>
              <Label>Motif du rejet <Required>*</Required></Label>
              <Tarea
                placeholder={
                  p.method === "mobile" ? "Numéro injoignable / opérateur indisponible / soupçon de fraude…" :
                  p.method === "bank"   ? "IBAN invalide / banque refuse / vérification KYC échouée…"        :
                  p.method === "paypal" ? "Compte PayPal inexistant / restriction / soupçon de fraude…"     :
                                          "Motif détaillé du rejet…"
                }
                value={reason}
                onChange={e => setReason(e.target.value)}
                style={{minHeight:"80px"}}
              />
              <div style={{fontSize:".7rem",color:"#ff3b5c",background:"rgba(255,59,92,.06)",
                border:"1px solid rgba(255,59,92,.2)",borderRadius:"6px",padding:".55rem .75rem",
                display:"flex",alignItems:"flex-start",gap:".4rem"}}>
                <span>⚠</span>
                <span>
                  Les <strong>{parseFloat(p.amount_evp).toFixed(0)} EVP</strong> seront
                  automatiquement remboursés au wallet de l'utilisateur.
                </span>
              </div>
            </Field>
          )}
        </MBody>
        <MFoot>
          <CancelBtn2 onClick={onClose}>Annuler</CancelBtn2>
          <SaveBtn
            disabled={acting || (isReject && !reason.trim()) || (type === "complete" && !ref.trim())}
            onClick={handle}
            style={isReject ? {background:"#ff3b5c",color:"#fff"} : {}}
          >
            {acting ? "…" :
              isReject ? "Rejeter & rembourser" :
              type === "approve" ? "Approuver" :
              "Confirmer paiement"}
          </SaveBtn>
        </MFoot>
      </ModalBox>
    </Overlay>
  );
}


export default function AdminDashboard({ user, onLogout, onBack }) {
  const [tab,        setTab]        = useState("overview");
  const [stats,      setStats]      = useState(null);
  const [categories, setCategories] = useState(CATEGORIES_DEFAULT);
  const [sideOpen,   setSideOpen]   = useState(false);   // mobile
  const [collapsed,  setCollapsed]  = useState(false);   // desktop
  const toast = useToasts();

  useEffect(() => {
    adminGetStats().then(r => setStats(r.data)).catch(() => setStats(MOCK_STATS));
    adminGetCategories()
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : (r.data.results || []);
        setCategories(data.length ? data : CATEGORIES_DEFAULT);
      })
      .catch(() => {});
  }, []);

  // Close mobile drawer on tab change
  const handleTab = (id) => { setTab(id); setSideOpen(false); };

  const pendingWd = stats?.pending_withdrawals || 0;

  // On mobile: sideOpen controls visibility
  // On desktop: collapsed controls whether sidebar is shown
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  return (
    <ThemeProvider theme={theme}>
      <Shell>
        {/* ── Toggle button ── */}
        <ToggleBtn
          $collapsed={collapsed}
          $open={sideOpen}
          onClick={() => {
            if (window.innerWidth <= 768) setSideOpen(o => !o);
            else setCollapsed(c => !c);
          }}
          title={collapsed || !sideOpen ? "Ouvrir le menu" : "Fermer le menu"}
        >
          {(sideOpen || !collapsed) ? "‹" : "›"}
        </ToggleBtn>

        {/* Mobile overlay */}
        <SideOverlay $open={sideOpen} onClick={() => setSideOpen(false)} />

        {/* Sidebar */}
        <Sidebar $open={sideOpen} $collapsed={collapsed}>
          <SideTop>
            <SideLogo onClick={onBack} title="Retour aux marchés">Predict<span>event</span></SideLogo>
            <SideRole>Admin Panel</SideRole>
          </SideTop>
          <Nav>
            {TABS.map(t => (
              <NavItem key={t.id} $active={tab === t.id} onClick={() => handleTab(t.id)}>
                <NavIcon>{t.icon}</NavIcon>
                {t.label}
                {t.id === "withdrawals" && pendingWd > 0 && <NavBadge $red>{pendingWd}</NavBadge>}
              </NavItem>
            ))}
          </Nav>
          <SideFooter>
            <UserChip>
              <UserDot>{(user?.username||"A").slice(0,2).toUpperCase()}</UserDot>
              <div>
                <div style={{fontWeight:600,color:"#e8edf2"}}>{user?.username||"Admin"}</div>
                <div style={{fontSize:".6rem",color:"#5a6a7a",textTransform:"uppercase",letterSpacing:".06em"}}>{user?.role||"Staff"}</div>
              </div>
            </UserChip>
            {onLogout && <SignOutBtn onClick={onLogout}>Sign out</SignOutBtn>}
          </SideFooter>
        </Sidebar>

        {/* Main */}
        <Main>
          {tab==="overview"    && <><PageTitle>Admin <span>Overview</span></PageTitle><PageSub>Platform health and quick actions</PageSub><OverviewTab stats={stats} pendingWd={pendingWd} setTab={setTab}/></>}
          {tab==="markets"     && <><PageTitle>Market <span>Management</span></PageTitle><PageSub>Create, edit, publish, close and resolve prediction markets</PageSub><MarketsTab categories={categories} toast={toast}/></>}
          {tab==="users"       && <><PageTitle>User <span>Management</span></PageTitle><PageSub>Assign roles and manage platform access</PageSub><UsersTab toast={toast}/></>}
          {tab==="countries"   && <><PageTitle>Users by <span>Country</span></PageTitle><PageSub>Répartition géographique des inscrits, du volume tradé et des trades</PageSub><CountriesTab toast={toast}/></>}
          {tab==="withdrawals" && <><PageTitle>Withdrawal <span>Requests</span></PageTitle><PageSub>Review and approve or reject crypto withdrawal requests</PageSub><WithdrawalsTab toast={toast}/></>}
          {tab==="categories"  && <><PageTitle>Market <span>Categories</span></PageTitle><PageSub>Manage the categories used to classify markets</PageSub><CategoriesTab categories={categories} setCategories={setCategories} toast={toast}/></>}
        {tab === "payouts" && (
          <>
            <PageTitle>Mobile / Bank / PayPal <span>Payouts</span></PageTitle>
            <PageSub>Validate manual withdrawal requests across all methods</PageSub>
            <PayoutsTab toast={toast}/>
          </>
        )}
        {tab === "referrals" && (
  <>
    <PageTitle>Referrals <span>Dashboard</span></PageTitle>
    <PageSub>Programme de parrainage : stats, qualification, commissions</PageSub>
    <ReferralsTab toast={toast}/>
  </>
)}
{tab === "emails" && (
  <>
    <PageTitle>Email <span>Campaigns</span></PageTitle>
    <PageSub>Envoie des campagnes ciblées et gère les emails transactionnels</PageSub>
    <EmailCampaignsTab toast={toast}/>
  </>
)}
        </Main>

        <ToastWrap>
          {toast.list.map(t => <ToastItem key={t.id} $t={t.t}>{t.msg}</ToastItem>)}
        </ToastWrap>
      </Shell>
    </ThemeProvider>
  );
}
